<template>
  <div>
    <FormModal v-if="showPopup" :closePopup="closePopup" />
    <Navbar  :openPopup="openPopup" />
    <div class="top-bubbles">

      <div class="blog-area wave-bg">
        <div class="container">
          <div class="contact-info_inner search_top_banner">
            <div class="banner-content">
              <!-- Banner remains visible immediately for better FCP/LCP -->
              <div class="title-section">
                <h1 class="main-title">
                  <span class="title-line text-white">Digital Assets for Modern Business</span>
                </h1>
                <p class="subtitle text-center">Build. Launch. Grow. AI-Powered Real-World Projects.</p>
              </div>

              <div class="search-section">
                <!-- Search UI is visible immediately -->
                <div class="search-container">
                  <div class="search-input-wrapper">
                    <div class="search-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                   <input 
                    type="text" 
                    v-model="searchQuery" 
                    class="search-input" 
                    :placeholder="animatedPlaceholder" 
                    @input="handleSearch"
                    @keyup.enter="handleSearch"
                    @focus="handleFocus" 
                    @blur="handleBlur"
                  />
                    <button @click="handleSearch" class="search-button" aria-label="Search">
                      <span class="button-text">Search</span>
                      <div class="button-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </div>
                    </button>
                  </div>
                  <!-- Search Results Dropdown -->
                  <div v-if="showSearchResults && filteredApps.length > 0" class="search-results-dropdown">
                    <div class="dropdown-header">
                      <span class="results-count">{{ filteredApps.length }} result{{ filteredApps.length > 1 ? 's' : '' }} found</span>
                    </div>
                    <div class="dropdown-content">
                      <div 
                        v-for="app in filteredApps" 
                        :key="app.slug" 
                        class="search-result-item" 
                        @mousedown.prevent="navigateToApp(app.slug)">
                        <div class="result-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </div>
                        <div class="result-text">
                          <span class="result-name">{{ app.name }}</span>
                          <span v-if="app.one_liner" class="result-description">{{ app.one_liner }}</span>
                        </div>
                        <div class="result-arrow">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="showSearchResults && searchQuery.trim() !== '' && filteredApps.length === 0" class="search-results-dropdown no-results">
                    <div class="no-results-content">
                      <div class="no-results-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </div>
                      <p class="no-results-text">No results found</p>
                      <p class="no-results-subtext">Try different keywords</p>
                    </div>
                  </div>
                </div>
                <!-- Category Chips -->
                <div class="category-chips-container">
                  <button 
                    class="category-chip" 
                    :class="{ 'active': selectedCategory === null }"
                    @click="filterByCategory(null)">
                    All
                  </button>
                  <button 
                    v-for="category in availableCategories" 
                    :key="category.slug"
                    class="category-chip" 
                    :class="{ 'active': selectedCategory === category.slug }"
                    @click="filterByCategory(category.slug)">
                    {{ category.name }}
                  </button>
                </div>
              </div>

              <div class="background-effects">
                <div class="gradient-orb orb-1"></div>
                <div class="gradient-orb orb-2"></div>
                <div class="gradient-orb orb-3"></div>
                <div class="mesh-grid"></div>
              </div>
            </div>
          </div>
          <div>
            <!-- Show skeleton cards while loading the main list -->
            <div v-if="loading" class="row justify-content-center project-row">
                <div class="col-xl-3 col-lg-3 col-md-4 col-sm-6" v-for="n in skeletonCount" :key="`loading-skeleton-${n}`">
                  <div class="skeleton-card">
                    <div class="skeleton-image"></div>
                    <div class="skeleton-content">
                      <div class="skeleton-category"></div>
                      <div class="skeleton-title"></div>
                      <div class="skeleton-title-short"></div>
                      <div class="skeleton-description"></div>
                      <div class="skeleton-chips">
                        <div class="skeleton-chip"></div>
                        <div class="skeleton-chip"></div>
                        <div class="skeleton-chip"></div>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            <div v-else>
              <div class="row justify-content-center project-row">
                <!-- No Data Message -->
                <div v-if="!appDATA?.data || appDATA.data.length === 0" class="col-12 text-center">
                  <div class="container">
                    <div class="no-data-wrapper">
                      <div class="no-data-icon">
                        <i class="bi bi-search"></i>
                      </div>
                      <div class="no-data-title">No Results Found</div>
                      <div class="no-data-subtitle">We couldn't find anything matching your search. Try using different
                        keywords.</div>
                      <button @click="$router.go(-1)" class="no-data-btn">Go Back</button>
                    </div>
                  </div>
                </div>
                <!-- Main Data -->
                <div class="col-xl-3 col-lg-3 col-md-4 col-sm-6" v-for="(data, index) in appDATA?.data" :key="data.id">
                  <div 
                    class="single-blog-post single-blog-post-model bg-change card-link-wrapper" 
                    @click="navigateToDetail(data.slug)">
                        <div class="pink-top-shape"></div>
                      <div class="image border-abstract">
                        <div class="app-images">
                          <div v-if="data.screenshot_1?.url && data.screenshot_2?.url" class="check-related">
                            <div class="phone phone-left">
                              <img :src="$backendUrl + getOptimizedImage(data.screenshot_1)"
                                :alt="data.screenshot_1?.alternativeText || data.title || data.name || 'App Screenshot 1'"
                                class="img-fluid left-img" 
                                :loading="index < 1 ? 'eager' : 'lazy'"
                                :fetchpriority="index < 1 ? 'high' : 'auto'"
                                decoding="async"
                                width="120"
                                height="220" />
                            </div>
                            <div class="phone phone-right">
                              <img :src="$backendUrl + getOptimizedImage(data.screenshot_2)"
                                :alt="data.screenshot_2?.alternativeText || data.title || data.name || 'App Screenshot 2'"
                                class="img-fluid right-img"
                                :loading="index < 1 ? 'eager' : 'lazy'"
                                :fetchpriority="index < 1 ? 'high' : 'auto'"
                                decoding="async"
                                width="120"
                                height="220" />
                            </div>
                          </div>
                          <img v-else-if="data.screenshot_1?.url" :src="$backendUrl + getOptimizedImage(data.screenshot_1)"
                            :alt="data.screenshot_1?.alternativeText || data.title || data.name || 'data Screenshot 1'"
                            class="img-fluid" style="width:100%;height:220px;object-fit:contain;"
                            :loading="index < 2 ? 'eager' : 'lazy'"
                            decoding="async"
                            width="280"
                            height="220" />
                          <img v-else-if="data.screenshot_2?.url" :src="$backendUrl + getOptimizedImage(data.screenshot_2)"
                            :alt="data.screenshot_2?.alternativeText || data.title || data.name || 'data Screenshot 2'"
                            class="img-fluid" style="width:100%;height:220px;object-fit:contain;"
                            :loading="index < 2 ? 'eager' : 'lazy'"
                            decoding="async"
                            width="280"
                            height="220" />
                          <div v-else class="image-placeholder">
                            <i class="ri-image-line"></i>
                          </div>
                        </div>
                      </div>
                      <!-- Pricing Display -->
                      <div class="card-price-display" v-if="getCardPrice(data)">
                        <div class="card-price-wrapper">
                          <div class="card-price-badge">
                            <!-- <span class="card-price-label">Starting at</span> -->
                            <div class="card-price-main"> 
                              <span class="card-price-symbol"><span class="hide-on-mobile">Start with </span>{{ getCardCurrency(data) }}</span>
                              <span class="card-price-amount">{{getCardPrice(data)}}</span>
                            </div>
                            <span v-if="getOriginalPrice(data, getCardPrice(data)) > 0" class="card-original-price">
                              <span class="original-price-text">{{ getCardCurrency(data) }}{{getOriginalPrice(data, getCardPrice(data))}}</span>
                            </span>
                            <span v-if="calculateDiscount(getOriginalPrice(data, getCardPrice(data)), getCardPrice(data)) > 0" class="card-discount-badge">
                              <span class="discount-text">{{calculateDiscount(getOriginalPrice(data, getCardPrice(data)), getCardPrice(data))}}%</span>
                              <span class="discount-label">OFF</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="content">
                        <ul class="meta" v-if="getDisplayTags(data) && getDisplayTags(data).length > 0">
                          <li v-if="getDisplayTags(data)[0]" class="d-flex align-items-center category-meta-item category-chip-auto">
                            <i class="ri-folder-line"></i>
                            <span 
                              class="category-text-content"
                              :title="getDisplayTags(data)[0]?.name">
                              {{ getDisplayTags(data)[0]?.name }}
                            </span>
                          </li>
                        </ul>
                        <!-- Fallback to categories if no tags available -->
                        <ul class="meta" v-else-if="data?.categorories && data.categorories.length > 0">
                          <li class="d-flex align-items-center category-meta-item category-chip-auto">
                            <i class="ri-folder-line"></i>
                            <span 
                              class="category-text-content"
                              :title="data.categorories[0]?.name">
                              {{ data.categorories[0]?.name }}
                            </span>
                          </li>
                        </ul>
                        <!-- Second tag above title -->
                        <div v-if="getDisplayTags(data) && getDisplayTags(data).length > 1" class="tag-above-title">
                          <span class="tag-simple">
                            {{ getDisplayTags(data)[1]?.name }}
                          </span>
                        </div>
                        <h3 :title="data?.title">
                          {{ data?.title }}
                        </h3>
                        <p :title="data?.one_liner || data?.description" class="card-description" v-if="data?.one_liner || data?.description">
                          {{ data.one_liner || data.description }}
                        </p>
                        <div class="chips-container" v-if="getAppChips(data) && getAppChips(data).length > 0">
                          <span 
                            class="chip" 
                            v-for="(dataitem, index) in getAppChips(data).slice(0, 3)" 
                            :key="`expertise-${data.id}-${index}-${dataitem.id || dataitem.name || dataitem}`">
                            <span class="chip-text">{{ dataitem.name || dataitem }}</span>
                          </span>
                          <span v-if="getAppChips(data).length > 3" class="chip">
                            <span class="chip-text">+{{ getAppChips(data).length - 3 }}</span>
                          </span>
                        </div>
                      </div>
                      <div class="pink-bottom-bar" v-if="data?.card_cta_btn?.length">
                        <div class="category-buttons">
                          <template v-for="(cta, index) in data?.card_cta_btn" :key="cta.id">
                            <!-- Link Only Button - Internal Link -->
                            <router-link 
                              v-if="cta.button_type === 'Link_only' && cta.link && isInternalLink(cta.link)"
                              :to="cta.link"
                              class="cta-action-btn"
                              @click.stop>
                              {{ cta.label || cta.title }}
                            </router-link>
                            <!-- Link Only Button - External Link -->
                            <a 
                              v-else-if="cta.button_type === 'Link_only' && cta.link && !isInternalLink(cta.link)"
                              :href="cta.link"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="cta-action-btn"
                              @click.stop>
                              {{ cta.label || cta.title }}
                            </a>
                            <!-- Contact Popup Button -->
                            <button 
                              v-else-if="cta.button_type === 'Contect_Popup'"
                              class="cta-action-btn"
                              @click.stop.prevent="openPopup">
                              {{ cta.label || cta.title }}
                            </button>
                            <!-- Schedule Calendly Popup Button -->
                            <button 
                              v-else-if="cta.button_type === 'Schedule_Calendly_Popup'"
                              class="cta-action-btn"
                              @click.stop.prevent="openCalendly">
                              {{ cta.label || cta.title }}
                            </button>
                            <!-- Fallback: Default button behavior -->
                            <button 
                              v-else
                              class="cta-action-btn" 
                              @click.stop.prevent="handleCtaClick(cta)">
                              {{ cta.label || cta.title }}
                            </button>
                            <span v-if="index < data?.card_cta_btn?.length - 1" class="cta-pipe-separator">|</span>
                          </template>
                        </div>
                        
                      </div>
                    </div>
                </div>
                
                <!-- Skeleton Loader Cards -->
                <div v-if="loadingMore" class="col-xl-3 col-lg-3 col-md-4 col-sm-6" v-for="n in skeletonCount" :key="`skeleton-${n}`">
                  <div class="skeleton-card">
                    <div class="skeleton-image"></div>
                    <div class="skeleton-content">
                      <div class="skeleton-category"></div>
                      <div class="skeleton-title"></div>
                      <div class="skeleton-title-short"></div>
                      <div class="skeleton-description"></div>
                      <div class="skeleton-chips">
                        <div class="skeleton-chip"></div>
                        <div class="skeleton-chip"></div>
                        <div class="skeleton-chip"></div>
                      </div>
                    </div>
                    <div class="skeleton-shimmer-overlay"></div>
                  </div>
                </div>
                
                <!-- Load More Button -->
                <div v-if="shouldShowLoadMoreButton" class="col-lg-12 col-md-12">
                  <div class="load-more-container">
                    <button 
                      @click="loadMore" 
                      class="load-more-btn"
                      :disabled="loadingMore">
                      <span v-if="!loadingMore">Load More</span>
                      <span v-else>Loading...</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      <!-- GEO Optimization & Capabilities Matrix Section -->
    <section id="meander-geo-capabilities" class="geo-capabilities-section">
      <div class="geo-container">
        
        <!-- WHO WE ARE CARD -->
        <div id="who-we-are" class="geo-section-wrapper who-we-are-wrapper">
          <div class="who-we-are-card">
            <div class="who-we-are-glow"></div>
            <div class="card-meta">
              <span class="badge-ai-pulsing">
                <span class="pulse-dot"></span>
                AI-Enabled
              </span>
              <span class="badge-tag">Identity</span>
            </div>
            <h2 class="geo-section-title">Who We Are</h2>
            <p class="geo-section-desc">
              We are <strong>Meander Software</strong>—an AI-enabled software development team. We build scalable web applications, custom mobile apps, and enterprise systems that help businesses grow. By combining modern AI automation with clean, reliable software engineering, we deliver practical digital products that work smoothly under heavy loads.
            </p>
            <!-- WHO WE ARE CTA -->
            <div class="who-we-are-cta-container">
              <router-link to="/who-we-are" class="who-we-are-cta-btn">
                <span>Who We Are</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="cta-arrow">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </router-link>
            </div>
            <div class="who-we-are-stats">
              <div class="stat-item">
                <span class="stat-val">AI+</span>
                <span class="stat-lbl">Software Enabled</span>
              </div>
              <div class="stat-item">
                <span class="stat-val">100%</span>
                <span class="stat-lbl">Scalable Code</span>
              </div>
              <div class="stat-item">
                <span class="stat-val">Global</span>
                <span class="stat-lbl">Delivery</span>
              </div>
            </div>
          </div>
        </div>

        <!-- WHAT WE DO SECTION -->
        <div id="what-we-do" class="geo-section-wrapper what-we-do-wrapper">
          <div class="section-header text-center">
            <span class="badge-tag">Capabilities</span>
            <h2 class="geo-section-title">What We Do</h2>
            <p class="geo-section-subtitle">We design, build, and deploy next-generation software architectures.</p>
          </div>
          
          <div class="geo-grid">
            <!-- 1. Mobile App Development -->
            <article class="service-card">
              <div class="service-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                  <line x1="12" y1="18" x2="12.01" y2="18"></line>
                </svg>
              </div>
              <div class="service-content">
                <h3 class="service-title">Mobile App Development</h3>
                <p class="service-desc">We build clean native and hybrid mobile apps for iOS and Android. Our focus is on fast performance, smooth animations, and intuitive layouts that users enjoy.</p>
              </div>
            </article>

            <!-- 2. SaaS Development -->
            <article class="service-card">
              <div class="service-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <div class="service-content">
                <h3 class="service-title">SaaS Development</h3>
                <p class="service-desc">We design and build secure, multi-tenant SaaS platforms. We handle everything from database architecture and subscription billing to secure user role permissions.</p>
              </div>
            </article>

            <!-- 3. AI & Workflow Automation -->
            <article class="service-card generative-ai-card">
              <div class="service-icon animate-glow">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                </svg>
              </div>
              <div class="service-content">
                <h3 class="service-title">AI & Workflow Automation</h3>
                <p class="service-desc">We integrate Large Language Models (LLMs), machine learning models, and custom AI agents with automated software pipelines, custom RPA bots, and database bridges. We help you eliminate manual work and keep operations running 24/7.</p>
              </div>
            </article>

            <!-- 4. DevOps -->
            <article class="service-card">
              <div class="service-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <div class="service-content">
                <h3 class="service-title">DevOps</h3>
                <p class="service-desc">We set up automated testing and seamless deployment pipelines. This makes your releases fast, reliable, and free from manual errors.</p>
              </div>
            </article>

            <!-- 5. Cloud Engineering -->
            <article class="service-card">
              <div class="service-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
                </svg>
              </div>
              <div class="service-content">
                <h3 class="service-title">Cloud Engineering</h3>
                <p class="service-desc">We design secure, cost-effective cloud setups on AWS, Google Cloud, and Azure. We make sure your systems scale up automatically when traffic spikes.</p>
              </div>
            </article>

            <!-- 6. SEO & Digital Marketing -->
            <article class="service-card">
              <div class="service-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M23 6l-9.5 9.5-5-5L1 18"></path>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
              </div>
              <div class="service-content">
                <h3 class="service-title">SEO & Digital Marketing</h3>
                <p class="service-desc">We boost your search visibility, drive targeted organic traffic, and optimize conversion rates. From technical SEO audits and schema markups to content strategy and growth marketing, we make sure your business ranks and grows.</p>
              </div>
            </article>
          </div>
        </div>

        <!-- WHO WE HELP SECTION -->
        <div id="who-you-help" class="geo-section-wrapper who-we-help-wrapper">
          <div class="section-header text-center">
            <span class="badge-tag">Target Markets</span>
            <h2 class="geo-section-title">Who We Help</h2>
            <p class="geo-section-subtitle">We build custom, scalable solutions for diverse company archetypes and industries.</p>
          </div>
          
          <div class="industry-grid">
            <!-- 1. Startups -->
            <article class="industry-card">
              <div class="industry-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <div class="industry-content">
                <h3 class="industry-title">Startups & Founders</h3>
                <p class="industry-desc">Rapid prototyping, custom MVPs, and scale-ready architectures for fast-growing companies.</p>
              </div>
            </article>

            <!-- 2. Enterprises -->
            <article class="industry-card">
              <div class="industry-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                  <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                  <line x1="6" y1="6" x2="6.01" y2="6"></line>
                  <line x1="6" y1="18" x2="6.01" y2="18"></line>
                </svg>
              </div>
              <div class="industry-content">
                <h3 class="industry-title">Global Enterprises</h3>
                <p class="industry-desc">Complex system integrations, legacy modernization, and highly secure, high-concurrency systems.</p>
              </div>
            </article>

            <!-- 3. SMBs -->
            <article class="industry-card">
              <div class="industry-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
              </div>
              <div class="industry-content">
                <h3 class="industry-title">SMBs</h3>
                <p class="industry-desc">Cost-effective business automation, customer CRM platforms, and database workflows.</p>
              </div>
            </article>

            <!-- 4. Healthcare -->
            <article class="industry-card">
              <div class="industry-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                </svg>
              </div>
              <div class="industry-content">
                <h3 class="industry-title">Healthcare</h3>
                <p class="industry-desc">Secure, HIPAA-compliant patient management portals, custom EHR connectors, and telehealth apps.</p>
              </div>
            </article>

            <!-- 5. Logistics -->
            <article class="industry-card">
              <div class="industry-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="1" y="3" width="15" height="13"></rect>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                  <circle cx="18.5" cy="18.5" r="2.5"></circle>
                </svg>
              </div>
              <div class="industry-content">
                <h3 class="industry-title">Logistics & Supply</h3>
                <p class="industry-desc">Live vehicle tracking, dispatch portals, real-time inventory management, and route planners.</p>
              </div>
            </article>

            <!-- 6. Fintech -->
            <article class="industry-card">
              <div class="industry-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
              </div>
              <div class="industry-content">
                <h3 class="industry-title">Fintech & Payments</h3>
                <p class="industry-desc">Secure payment processing gateways, custom financial ledgers, and transaction dashboards.</p>
              </div>
            </article>

            <!-- 7. Education -->
            <article class="industry-card">
              <div class="industry-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                  <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"></path>
                </svg>
              </div>
              <div class="industry-content">
                <h3 class="industry-title">Education & E-learning</h3>
                <p class="industry-desc">Scalable learning management systems (LMS), virtual student portals, and content delivery grids.</p>
              </div>
            </article>

            <!-- 8. E-commerce & Retail -->
            <article class="industry-card">
              <div class="industry-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
              </div>
              <div class="industry-content">
                <h3 class="industry-title">E-commerce & Retail</h3>
                <p class="industry-desc">Custom storefronts, high-traffic multi-vendor marketplaces, and automated inventory systems.</p>
              </div>
            </article>
          </div>
        </div>

        <!-- FAQ Section is injected as JSON-LD Schema in the head for Google search rich snippets, not visible to end-users in UI -->

      </div>
    </section>

    <FooterStyleFour />
  </div>
</template>

<style>
.outer-animated-border {
  position: relative;
  padding: 3px; /* border thickness */
  border-radius: 16px;
  background: linear-gradient(270deg, #ED2775, #FF7448, #ED2775);
  background-size: 400% 400%;
  animation: animatedBorder 6s ease infinite;
  width: 100%;
  max-width: 600px;
  margin: auto;
  z-index: 1;
}

@keyframes animatedBorder {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
.no-data-wrapper {
  min-height: 70vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-align: center;
  padding: 40px 20px;
}

.no-data-icon {
  width: 120px;
  height: 120px;
  background: linear-gradient(26.1deg, #ED2775 0%, #FF7448 100%);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.no-data-icon i {
  font-size: 48px;
  color: white;
}

.no-data-title {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 10px;
}

.no-data-subtitle {
  font-size: 16px;
  color: #6c757d;
  margin-bottom: 25px;
}

.no-data-btn {
  background: linear-gradient(26.1deg, #ED2775 0%, #FF7448 100%);
  border: none;
  padding: 10px 25px;
  color: white;
  border-radius: 4px;
  font-weight: 500;
  transition: background 0.3s ease;
}

.no-data-btn:hover {
  background: linear-gradient(26.1deg, #FF7448 0%, #ED2775 100%);
}

/* Modern Search Banner Styles */
.search_top_banner {
  position: relative;
  padding: 80px 20px;
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
    background: linear-gradient(247.32deg, rgb(68, 0, 178), rgb(139, 79, 235) 70.53%);
  margin-bottom: 60px;
  border-radius: 30px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  padding: 40px 20px 10px !important;
  width: 100%;
  max-width: 890px;
  margin: 0 auto;
  z-index: 11111;
}

.banner-content {
  position: relative;
  z-index: 10;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

/* Title Section */
.title-section {
  margin-bottom: 25px;
  animation: fadeInUp 1s ease-out;
  position: relative;
  z-index: 10;
}

.main-title {
  font-size: clamp(2.5rem, 5vw, 4rem) !important;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 20px;
  color: #ffffff;
  font-size: 40px !important;
  white-space: nowrap;
}

.title-line {
  display: block;
 /* background: linear-gradient(135deg, #ffffff 0%, #fddde6 50%, #f472b6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent; */

  /* For Firefox */
  /* background-clip: text;
  color: transparent;
  animation: gradientShift 3s ease-in-out infinite; */
}

.subtitle {
  font-size: 1.2rem !important;
  color: #fff;
  font-weight: 400;
  animation: fadeInUp 1s ease-out 0.2s both;
  text-align: left;
}

/* Search Section */
.search-section {
  margin-bottom: 30px;
  animation: fadeInUp 1s ease-out 0.4s both;
}

.search-container {
  max-width: 600px;
  margin: 0 auto;
  position: relative; /* Added for dropdown positioning */
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  padding: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  height: 60px;
}

.search-input-wrapper:focus-within {
  border-color: #ff9960;
  box-shadow: 0 8px 32px rgba(96, 165, 250, 0.3), 0 0 0 4px rgba(96, 165, 250, 0.1);
  transform: translateY(-2px);
}

.search-icon {
  padding: 0 20px;
  color: #fff;
  transition: color 0.3s ease;
}

.search-input-wrapper:focus-within .search-icon {
  color: #fff;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 1.1rem;
  padding: 20px 0;
}

.search-input::placeholder {
  color: #fff;
  transition: color 0.3s ease;
}

.search-input:focus::placeholder {
  color: #fff;
}

.search-button {
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  border: none;
  border-radius: 40px;
  padding: 13px 30px;
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 15px rgba(96, 165, 250, 0.4);
      position: absolute;
    right: 3px;
    top: 3px;
}

.search-button:hover {
     background-color: #ED2775;
    color: #fff;
}

.search-button:active {
  transform: translateY(0);
}

.button-icon {
  transition: transform 0.3s ease;
}

.search-button:hover .button-icon {
  transform: translateX(3px);
}



/* Background Effects */
.background-effects {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.6;
  animation: orbFloat 20s ease-in-out infinite;
}

.orb-1 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, #60a5fa 0%, transparent 70%);
  top: -150px;
  left: -150px;
  animation-delay: 0s;
}

.orb-2 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, #a78bfa 0%, transparent 70%);
  top: 50%;
  right: -100px;
  animation-delay: 7s;
}

.orb-3 {
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, #f472b6 0%, transparent 70%);
  bottom: -125px;
  left: 50%;
  animation-delay: 14s;
}

.mesh-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 30s linear infinite;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes gradientShift {
  0%, 100% {
    filter: hue-rotate(0deg);
  }
  50% {
    filter: hue-rotate(30deg);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes orbFloat {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

@keyframes gridMove {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(50px, 50px);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-title {
    white-space: normal !important;
    font-size: 28px !important;
  }
  .search_top_banner {
    padding: 60px 20px;
    width: auto !important;
  }
  
  .search-results-dropdown {
    max-height: 250px;
    border-radius: 16px;
  }
  
  .search-result-item {
    padding: 12px 14px;
    gap: 10px;
  }
  
  .result-icon {
    width: 32px;
    height: 32px;
  }
  
  .result-name {
    font-size: 14px;
  }
  
  .result-description {
    font-size: 11px;
  }
  
  .search-input-wrapper {
    /* flex-direction: column; */
    gap: 15px;
    padding: 10px;
    height: 38px; 

    .search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 11px;
  padding: 10px 0;
}
  }
  
  .search-button {
    /* width: 100%; */
    justify-content: center;
  }
  
  .category-chips-container {
    flex-wrap: wrap !important;
    justify-content: center !important;
    gap: 8px;
    margin: 15px auto 0 auto !important;
    padding: 0 5px;
    max-width: 100% !important;
  }
  
  .category-chip {
    padding: 6px 16px;
    font-size: 12px;
  }
  
  .floating-card {
    padding: 10px 15px;
  }
  
  .card-1, .card-2, .card-3 {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    bottom: auto;
    transform: none;
    margin: 10px auto;
  }
  
  .floating-elements {
    height: auto;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
}




/* Search Results Dropdown Styles - Modern Glassmorphism Design */
.search-results-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.15),
    0 8px 16px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  max-height: 300px;
  overflow: hidden;
  z-index: 109999;
  margin-top: 2px;
  animation: slideDown 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-header {
  padding: 12px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, rgba(237, 39, 117, 0.05) 0%, rgba(255, 116, 72, 0.05) 100%);
  text-align: left;
}

.results-count {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  text-align: left;
}

.dropdown-content {
  max-height: 340px;
  overflow-y: auto;
  padding: 8px;
  text-align: left;
}

.dropdown-content::-webkit-scrollbar {
  width: 6px;
}

.dropdown-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.dropdown-content::-webkit-scrollbar-thumb {
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  border-radius: 10px;
}

.dropdown-content::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(44.44deg, #FF7448 7.79%, #ED2775 94.18%);
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin: 4px 0;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent;
  border: 1px solid transparent;
  position: relative;
  overflow: hidden;
  text-align: left;
  justify-content: flex-start;
}

.search-result-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(237, 39, 117, 0.1), transparent);
  transition: left 0.5s ease;
}

.search-result-item:hover::before {
  left: 100%;
}

.search-result-item:hover {
  background: linear-gradient(135deg, rgba(237, 39, 117, 0.08) 0%, rgba(255, 116, 72, 0.08) 100%);
  border-color: rgba(237, 39, 117, 0.2);
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(237, 39, 117, 0.15);
}

.result-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  color: white;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.search-result-item:hover .result-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 4px 12px rgba(237, 39, 117, 0.3);
}

.result-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  text-align: left;
  align-items: flex-start;
}

.result-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.3s ease;
  text-align: left;
  width: 100%;
}

.search-result-item:hover .result-name {
  color: #ED2775;
}

.result-description {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  width: 100%;
}

.result-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(237, 39, 117, 0.1);
  color: #ED2775;
  flex-shrink: 0;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateX(-8px);
}

.search-result-item:hover .result-arrow {
  opacity: 1;
  transform: translateX(0);
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  color: white;
}

/* No Results State */
.search-results-dropdown.no-results {
  padding: 40px 20px;
  text-align: center;
}

.no-results-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.no-results-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(237, 39, 117, 0.1) 0%, rgba(255, 116, 72, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ED2775;
  margin-bottom: 8px;
}

.no-results-text {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.no-results-subtext {
  font-size: 13px;
  color: #666;
  margin: 0;
}

/* Category Chips Container */
.category-chips-container {
  display: flex;
  flex-wrap: nowrap; /* Single line */
  overflow-x: auto; /* Horizontal scroll if needed */
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  gap: 6px 8px;
  margin: 24px -20px 0 -20px; /* Breakout parent banner padding */
  padding: 10px 20px; /* Internal padding to keep aligned */
  max-width: calc(100% + 40px);
}

@media (min-width: 992px) {
  .category-chips-container {
    justify-content: safe center; /* Center on desktop when it fits, switch to left-align when overflowing */
  }
}

.category-chips-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.category-chip {
    flex-shrink: 0; /* Prevent chips from squeezing */
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 30px;
    padding: 4px 10px; /* Compact padding */
    color: #ffffff;
    font-size: 11px; /* Compact font size to fit all 8 chips */
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
    white-space: nowrap;
    position: relative;
    overflow: hidden;
}

.category-chip::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.category-chip:hover::before {
  left: 100%;
}

.category-chip:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.category-chip.active {
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  border-color: transparent;
  box-shadow: 0 4px 20px rgba(237, 39, 117, 0.45);
  font-weight: 600;
}

.category-chip.active:hover {
  background: linear-gradient(44.44deg, #FF7448 7.79%, #ED2775 94.18%);
  transform: translateY(-2px);
  box-shadow: 0 6px 22px rgba(237, 39, 117, 0.55);
}

.category-chip:active {
  transform: translateY(0);
}

/* Mobile specific category chip styling */
@media (max-width: 767px) {
  .category-chip {
    padding: 6px 14px;
    font-size: 12px;
    border-radius: 20px;
  }
  .category-chips-container {
    flex-wrap: wrap !important;
    justify-content: center !important;
    gap: 8px 10px;
    padding: 5px 0;
    margin: 20px auto 0 auto !important;
    max-width: 100% !important;
  }
}

@media (max-width: 500px) {
  .category-chip {
    padding: 5px 12px;
    font-size: 11px;
  }
  .category-chips-container {
    flex-wrap: wrap !important;
    justify-content: center !important;
    gap: 6px 8px;
    margin: 15px auto 0 auto !important;
  }
}


@media only screen and (max-width: 500px) {
    .search-section {
        margin-bottom: 5px;
    }
}
/* Mobile responsive adjustments for search dropdown */


.blog-area {
  padding-top: 120px;
}

.wave-bg {
  position: relative;
  min-height: 100vh;
  background: #fff;
  /* or any base color */
  overflow: hidden;
  z-index: 0;
}

/* Top wave (normal orientation) */
.wave-bg::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 200%;
  height: 100vh;
  background: url("data:image/svg+xml;utf8,<svg width='100%' height='500' viewBox='0 0 1200 200' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><defs><linearGradient id='grad' x1='0%' y1='0%' x2='100%' y2='0%'><stop offset='0%' stop-color='%239F5FF1'/><stop offset='50%' stop-color='%23FF54B0'/><stop offset='100%' stop-color='%23FF9F5A'/></linearGradient></defs><path d='M0,100 C300,200 900,0 1200,100 L1200,0 L0,0 Z' fill='url(%23grad)' opacity='0.9'/></svg>") repeat-x;
  background-size: cover;
  animation: waveParallaxTop 10s linear infinite;
  z-index: -1;
  pointer-events: none;
  /* No rotation for top wave */
}

/* Top wave (slower, faded) */
.wave-bg::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 200%;
  height: 100vh;
  background: url("data:image/svg+xml;utf8,<svg width='100%' height='500' viewBox='0 0 1200 200' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><defs><linearGradient id='grad' x1='0%' y1='0%' x2='100%' y2='0%'><stop offset='0%' stop-color='%239F5FF1'/><stop offset='50%' stop-color='%23FF54B0'/><stop offset='100%' stop-color='%23FF9F5A'/></linearGradient></defs><path d='M0,100 C300,200 900,0 1200,100 L1200,0 L0,0 Z' fill='url(%23grad)' opacity='0.9'/></svg>") repeat-x;
  background-size: cover;
  animation: waveParallaxTopSlow 20s linear infinite;
  opacity: 0.7;
  z-index: -2;
  /* No rotation for top wave */
}

/* Bottom wave (reverse, using new pseudo-elements) */
/* Add bottom waves */
.wave-bg::before,
.wave-bg::after,
.wave-bg::bottom-before,
.wave-bg::bottom-after {
  pointer-events: none;
}

/* Bottom wave (reverse orientation) */
.wave-bg::bottom-before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%;
  height: 100vh;
  background: url("data:image/svg+xml;utf8,<svg width='100%' height='500' viewBox='0 0 1200 200' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><defs><linearGradient id='grad' x1='0%' y1='0%' x2='100%' y2='0%'><stop offset='0%' stop-color='%239F5FF1'/><stop offset='50%' stop-color='%23FF54B0'/><stop offset='100%' stop-color='%23FF9F5A'/></linearGradient></defs><path d='M0,100 C300,200 900,0 1200,100 L1200,0 L0,0 Z' fill='url(%23grad)' opacity='0.9'/></svg>") repeat-x;
  background-size: cover;
  animation: waveParallaxBottom 10s linear infinite;
  z-index: 1;
  transform: rotate(180deg);
  opacity: 1;
}

/* Bottom wave (slower, faded, reverse orientation) */
.wave-bg::bottom-after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%;
  height: 100vh;
  background: url("data:image/svg+xml;utf8,<svg width='100%' height='500' viewBox='0 0 1200 200' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><defs><linearGradient id='grad' x1='0%' y1='0%' x2='100%' y2='0%'><stop offset='0%' stop-color='%239F5FF1'/><stop offset='50%' stop-color='%23FF54B0'/><stop offset='100%' stop-color='%23FF9F5A'/></linearGradient></defs><path d='M0,100 C300,200 900,0 1200,100 L1200,0 L0,0 Z' fill='url(%23grad)' opacity='0.9'/></svg>") repeat-x;
  background-size: cover;
  animation: waveParallaxBottomSlow 20s linear infinite;
  opacity: 0.5;
  z-index: -2;
  transform: rotate(180deg);
}

/* Keyframes for top waves (left to right) */
@keyframes waveParallaxTop {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-50%);
  }
}

@keyframes waveParallaxTopSlow {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-25%);
  }
}

/* Keyframes for bottom waves (right to left, reversed) */
@keyframes waveParallaxBottom {
  from {
    transform: translateX(0) rotate(180deg);
  }

  to {
    transform: translateX(50%) rotate(180deg);
  }
}

@keyframes waveParallaxBottomSlow {
  from {
    transform: translateX(0) rotate(180deg);
  }

  to {
    transform: translateX(25%) rotate(180deg);
  }
}



.single-blog-post .content ul li:not(.tag-chip):not(.tag-simple) {
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  padding: 2px 10px !important;
  width: max-content;
  gap: 6px;
  color: #fff !important;
  font-size: 11px;
  border-radius: 6px;
  position: relative;
  display: inline-flex;
  align-items: center;
}

.single-blog-post .content ul li.tag-simple {
  background: transparent !important;
  padding: 0 !important;
  color: #ED2775 !important;
  font-size: 11px;
  border-radius: 6px;
  position: relative;
  width: 100%;
  align-items: center;
}

.single-blog-post .content ul li i {
  color: #fff !important;
  position: unset !important;
}

.bg-change {
  box-shadow: rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px;
  background: #e8dff3;
  border-radius: 12px;
  text-align: center;
  padding: 40px 20px 30px;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}
.bg-change .image {
  overflow: unset !important;
  height: 220px;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.bg-change .image .app-images {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.border-abstract {
  border-bottom: 1px solid #eee;
  border-radius: 100px;
  box-shadow: rgb(17 17 26 / 7%) 0px 1px 0px;
  z-index: 11;
}

.image-placeholder {
  width: 100%;
  height: 220px;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 8px;
}

.image-placeholder i {
  font-size: 48px;
  color: rgba(233, 30, 99, 0.3);
}

.widget-area .widget_meander_software_posts_thumb .item {
  border-bottom: 1px solid rgba(242, 60, 105, 0.19);
  padding-bottom: 10px;
}
.single-blog-post-model {
  position: relative;
  overflow: hidden;
  background: #fff;
  border-radius: 8px;
  transition: transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.5s ease, z-index 0s;
  height: 100%;
  display: flex;
  flex-direction: column;
  transform: scale(1);
  z-index: 1;
}

/* Info Icon Styling */
.info-icon-wrapper {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
}

.info-icon-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 
    4px 4px 8px rgba(0, 0, 0, 0.2);
}

.info-icon-wrapper:active {
  transform: translateY(0px);
  box-shadow: 
    2px 2px 4px rgba(0, 0, 0, 0.15),
    -1px -1px 2px rgba(255, 255, 255, 0.08),
    inset 2px 2px 4px rgba(0, 0, 0, 0.15),
    inset -1px -1px 2px rgba(255, 255, 255, 0.1);
}

.info-icon-wrapper i {
  color: #fff;
  font-size: 14px;
  line-height: 1;
}

/* Chips Styling */
.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 12px;
  min-height: 32px;
  align-items: center;
  /* justify-content: center; */
  flex-shrink: 0;
}

.chip {
  display: inline-flex;
  align-items: center;
  text-transform: capitalize;
  justify-content: center;
  padding: 4px 11px;
  position: relative;
  border-radius: 25px;
  font-size: 8px;
  font-weight: 600;
  color: #000000;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: 6px;
  cursor: pointer;
  overflow: hidden;
  
  /* 3D Neumorphic Effect */
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(240, 240, 250, 0.8));
  box-shadow: 
    4px 4px 8px rgba(0, 0, 0, 0.1),
    -4px -4px 8px rgba(255, 255, 255, 0.9),
    inset 2px 2px 4px rgba(255, 255, 255, 0.5),
    inset -2px -2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.3);
  
  /* Gradient overlay for theme colors */
  background-image: 
    linear-gradient(135deg, rgba(237, 39, 117, 0.08) 0%, rgba(255, 116, 72, 0.08) 50%, rgba(139, 79, 235, 0.08) 100%);
}

.chip::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 25px;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.3) 0%, 
    transparent 50%, 
    rgba(0, 0, 0, 0.05) 100%);
  pointer-events: none;
  opacity: 0.6;
}

.chip-text {
  position: relative;
  z-index: 1;
  letter-spacing: 0.3px;
  font-weight: lighter;
  color: #000000;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.chip-pipe {
  position: relative;
  z-index: 1;
  opacity: 0.4;
  font-weight: 300;
  font-size: 14px;
  margin: 0 2px;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.chip:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 
    6px 6px 12px rgba(0, 0, 0, 0.15),
    -6px -6px 12px rgba(255, 255, 255, 1),
    inset 2px 2px 4px rgba(255, 255, 255, 0.6),
    inset -2px -2px 4px rgba(0, 0, 0, 0.08);
  background-image: 
    linear-gradient(135deg, rgba(237, 39, 117, 0.12) 0%, rgba(255, 116, 72, 0.12) 50%, rgba(139, 79, 235, 0.12) 100%);
}

.chip:active {
  transform: translateY(0px) scale(0.98);
  box-shadow: 
    2px 2px 4px rgba(0, 0, 0, 0.1),
    -2px -2px 4px rgba(255, 255, 255, 0.8),
    inset 3px 3px 6px rgba(0, 0, 0, 0.1),
    inset -3px -3px 6px rgba(255, 255, 255, 0.5);
}

/* Category Chip Auto-width - fits text content */
.category-chip-auto {
  max-width: none !important;
  width: fit-content;
}

.category-chip-auto .category-text-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

/* Card Pricing Display Styles - Simple price by default, full chip on hover */
.card-price-display {
  padding: 5px 0;
    position: absolute;
    top: 265px;
    right: 0;
}

.card-price-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}

.card-price-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 14px;
  position: relative;
  border-radius: 20px 0 0 20px;
  background: linear-gradient(135deg, #ED2775 0%, #FF7448 100%);
  border: none;
  box-shadow: 
    0 2px 8px rgba(237, 39, 117, 0.2),
    0 0 0 0.5px rgba(255, 255, 255, 0.5);
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  flex-wrap: nowrap;
  transform: translateX(0);
  overflow: visible;
  width: fit-content;
  min-width: fit-content;
}

.card-price-badge::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 22px;
  background: radial-gradient(ellipse at center, 
    rgba(255, 255, 255, 0.7) 0%, 
    rgba(255, 255, 255, 0.4) 30%, 
    rgba(255, 255, 255, 0.1) 60%, 
    transparent 100%);
  pointer-events: none;
  z-index: -1;
  filter: blur(6px);
  opacity: 0.9;
}

.card-price-badge::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 20px;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.15) 0%, 
    transparent 50%, 
    rgba(255, 255, 255, 0.1) 100%);
  pointer-events: none;
  z-index: 1;
}

.single-blog-post:hover .card-price-badge::before {
  opacity: 0.2;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.15) 0%, 
    transparent 50%, 
    rgba(0, 0, 0, 0.05) 100%);
}

.card-price-label {
  font-size: 9px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: absolute;
  z-index: 1;
  white-space: nowrap;
  opacity: 0;
  transform: translateX(20px);
  width: 0;
  overflow: hidden;
  transition: 
    opacity 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s,
    transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s,
    width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s;
  pointer-events: none;
}

.card-price-main {
  display: flex;
  align-items: baseline;
  gap: 1px;
  position: relative;
  z-index: 1;
  white-space: nowrap;
}

.card-price-symbol {
  font-size: 11px;
  color: #ffffff;
  font-weight: 700;
  line-height: 1;
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.card-price-amount {
  font-size: 15px;
  color: #ffffff;
  font-weight: 800;
  line-height: 1;
  background: none;
  -webkit-text-fill-color: #ffffff;
  background-clip: unset;
  white-space: nowrap;
  transition: font-size 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.card-discount-badge {
  display: inline-flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  color: #ffffff !important;
  font-size: 8px;
  font-weight: 800;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  line-height: 1.1;
  box-shadow: 
    0 2px 4px rgba(237, 39, 117, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.2);
  position: absolute;
  z-index: 1;
  min-width: 0;
  opacity: 0;
  transform: translateX(20px) scale(0.8);
  width: 0;
  overflow: hidden;
  padding: 0;
  transition: 
    opacity 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    padding 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    min-width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s;
  pointer-events: none;
}

.card-discount-badge .discount-text,
.card-discount-badge .discount-label {
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.card-discount-badge .discount-text {
  font-size: 9px;
  font-weight: 900;
  display: block;
  line-height: 1;
}

.card-discount-badge .discount-label {
  font-size: 6px;
  font-weight: 600;
  display: block;
  opacity: 0.95;
  margin-top: 1px;
}

.card-original-price {
  display: inline-flex;
  align-items: center;
  font-size: 8px;
  color: #bbb;
  font-weight: 400;
  white-space: nowrap;
  position: absolute;
  z-index: 1;
  opacity: 0;
  transform: translateX(20px);
  width: 0;
  overflow: hidden;
  margin-left: 0;
  transition: 
    opacity 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.08s,
    transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.08s,
    width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.08s;
  pointer-events: none;
}

.card-original-price .original-price-text {
  text-decoration: line-through;
  text-decoration-color: #ccc;
  text-decoration-thickness: 1px;
  position: relative;
}

.card-original-price .original-price-text::after {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, #ccc 0%, transparent 100%);
  opacity: 0.6;
}

/* Show full chip on card hover with smooth slide effect */
.single-blog-post:hover .card-price-badge,
.single-blog-post:active .card-price-badge {
  gap: 4px;
  padding: 6px 10px;
  flex-wrap: nowrap;
  transform: translateY(-2px);
  background: linear-gradient(135deg, #7C3AED 0%, #8B5CF6 50%, #C026D3 100%);
  box-shadow:
    0 4px 12px rgba(124, 58, 237, 0.4),
    0 0 0 0.5px rgba(255, 255, 255, 0.6);
}

.single-blog-post:hover .card-price-badge::before,
.single-blog-post:active .card-price-badge::before {
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  filter: blur(6px);
  opacity: 1;
}

.single-blog-post:hover .card-price-symbol,
.single-blog-post:active .card-price-symbol {
  font-size: 12px;
}

.single-blog-post:hover .card-price-amount,
.single-blog-post:active .card-price-amount {
  font-size: 16px;
}

.single-blog-post:hover .card-price-label,
.single-blog-post:active .card-price-label {
  opacity: 1;
  transform: translateX(0);
  width: auto;
  position: relative;
  pointer-events: auto;
  transition:
    opacity 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s,
    transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s,
    width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.05s;
}

.single-blog-post:hover .card-price-main .card-price-symbol,
.single-blog-post:active .card-price-main .card-price-symbol {
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.single-blog-post:hover .card-price-main .card-price-amount,
.single-blog-post:active .card-price-main .card-price-amount {
  color: #ffffff !important;
  background: none;
  -webkit-text-fill-color: #ffffff;
  background-clip: unset;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}


.single-blog-post:hover .card-original-price,
.single-blog-post:active .card-original-price {
  opacity: 0.9;
  transform: translateX(0);
  width: auto;
  position: relative;
  margin-left: 2px;
  color: rgba(255, 255, 255, 0.85);
  pointer-events: auto;
  transition:
    opacity 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.08s,
    transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.08s,
    width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.08s;
}

.single-blog-post:hover .card-discount-badge,
.single-blog-post:active .card-discount-badge {
  opacity: 1;
  transform: translateX(0) scale(0.95);
  width: auto;
  min-width: 32px;
  padding: 3px 6px;
  position: relative;
  pointer-events: auto;
  background: #ffffff;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.15),
    inset 0 1px 2px rgba(0, 0, 0, 0.05);
  transition:
    opacity 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    padding 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s,
    min-width 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s;
}

.single-blog-post:hover .card-discount-badge .discount-text,
.single-blog-post:active .card-discount-badge .discount-text {
  background: linear-gradient(135deg, #ED2775 0%, #FF7448 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.single-blog-post:hover .card-discount-badge .discount-label,
.single-blog-post:active .card-discount-badge .discount-label {
  background: linear-gradient(135deg, #ED2775 0%, #FF7448 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

@media (max-width: 575px) {
  /* .card-price-display {
    padding: 8px 12px;
  } */

  .card-price-wrapper {
    gap: 4px;
  }

  .card-price-badge {
    padding: 5px 10px;
    gap: 6px;
  }

  .card-price-label {
    font-size: 8px;
  }

  .card-price-symbol {
    font-size: 12px;
  }

  .card-price-amount {
    font-size: 18px;
  }

  .card-discount-badge {
    padding: 3px 6px;
    min-width: 32px;
  }

  .card-discount-badge .discount-text {
    font-size: 9px;
  }

  .card-discount-badge .discount-label {
    font-size: 6px;
  }

  .card-original-price {
    font-size: 7px;
    margin-left: 1px;
    opacity: 0.65;
  }

  .card-original-price .original-price-text::after {
    height: 0.5px;
  }

  /* Apply hover effects by default on mobile */
  .single-blog-post-model {
    transform: scale(1.016);
    z-index: 10;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  .hide-on-mobile {
    display: none !important;
  }

  .single-blog-post .pink-top-shape {
    top: 0;
    transform: translateY(0) !important;
  }

  .single-blog-post .pink-bottom-bar {
    bottom: 0;
    transform: translateY(0) !important;
  }

  .single-blog-post .card-price-badge {
    gap: 8px;
    padding: 6px 12px;
    flex-wrap: wrap;
    transform: translateY(-2px);
    background: linear-gradient(135deg, #7C3AED 0%, #8B5CF6 50%, #C026D3 100%);
    box-shadow:
      0 4px 12px rgba(124, 58, 237, 0.4),
      0 0 0 0.5px rgba(255, 255, 255, 0.6);
  }

  .single-blog-post .card-price-badge::before {
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    filter: blur(8px);
    opacity: 1;
  }

  .single-blog-post .card-price-symbol {
    font-size: 14px;
  }

  .single-blog-post .card-price-amount {
    font-size: 20px;
  }

  .single-blog-post .card-price-label {
    opacity: 1;
    transform: translateX(0);
    width: auto;
    position: relative;
    pointer-events: auto;
  }

  .single-blog-post .card-price-main .card-price-symbol {
    color: #ffffff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  .single-blog-post .card-price-main .card-price-amount {
    color: #ffffff !important;
    background: none;
    -webkit-text-fill-color: #ffffff;
    background-clip: unset;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  .single-blog-post .card-original-price {
    opacity: 0.9;
    transform: translateX(0);
    width: auto;
    position: relative;
    margin-left: 2px;
    color: rgba(255, 255, 255, 0.85);
    pointer-events: auto;
  }

  .single-blog-post .card-discount-badge {
    opacity: 1;
    transform: translateX(0) scale(1);
    width: auto;
    min-width: 36px;
    padding: 4px 8px;
    position: relative;
    pointer-events: auto;
    background: #ffffff;
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.15),
      inset 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .single-blog-post .card-discount-badge .discount-text {
    background: linear-gradient(135deg, #ED2775 0%, #FF7448 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }

  .single-blog-post .card-discount-badge .discount-label {
    background: linear-gradient(135deg, #ED2775 0%, #FF7448 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }

  .single-blog-post .category-meta-item {
    background: #ffffff !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .single-blog-post .category-meta-item .category-text-content,
  .single-blog-post .category-meta-item .category-text-content .marquee-text,
  .single-blog-post .category-meta-item i {
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
    background: none !important;
  }

  .single-blog-post .content ul li.tag-chip {
    border-color: rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    color: #e91e63 !important;
  }

  .single-blog-post .content ul li.tag-chip::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    z-index: -1;
  }
}

/* Card row spacing */
.project-row {
  margin-left: -10px;
  margin-right: -10px;
  display: flex;
  flex-wrap: wrap;
}

.project-row > [class*="col-"] {
  padding-left: 20px;
  padding-right: 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  position: relative;
}

/* Responsive adjustments for cards */
@media (max-width: 991px) {
  .project-row > [class*="col-"] {
    margin-bottom: 20px;
  }
  /* Optimization: Disable heavy background waves on mobile */
  .wave-bg::bottom-before,
  .wave-bg::bottom-after {
    display: none !important;
  }
  .wave-bg::before,
  .wave-bg::after {
    height: 60vh; /* Shorter waves on mobile */
  }
}

@media (max-width: 767px) {
  .col-md-6 {
    margin-bottom: 15px;
  }
  
  .single-blog-post .content h3,
  .content h3 {
    font-size: 15px !important;
    min-height: 45px !important; /* 15px * 1.5 * 2 = 45px for exactly 2 lines */
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    white-space: normal !important;
  }
  
  .card-description {
    font-size: 11px;
    min-height: 30.8px; /* 11px * 1.4 * 2 = 30.8px for exactly 2 lines */
    max-height: 30.8px;
  }
  
  .cta-action-btn {
    font-size: 11px;
    padding: 5px 12px;
  }
  
  .chip {
    font-size: 10px;
    padding: 5px 10px;
  }
  
  .chips-container {
    gap: 6px;
    margin-top: 10px;
  }
}

@media (max-width: 575px) {
  .pink-bottom-bar {
    bottom: 0 !important;
  }
  .pink-top-shape {
    top: 0 !important;
  }
  
  .single-blog-post .content h3,
  .content h3 {
    font-size: 14px !important;
    min-height: 42px !important; /* 14px * 1.5 * 2 = 42px for exactly 2 lines */
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    white-space: normal !important;
  }
  
  .card-description {
    font-size: 10px;
    min-height: 28px; /* 10px * 1.4 * 2 = 28px for exactly 2 lines */
    max-height: 28px;
  }
  
  .chip {
    font-size: 9px;
    padding: 4px 8px;
  }
  
  .chips-container {
    gap: 5px;
  }
}
/* Top pink shape */
.pink-top-shape {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 190px;
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  border-bottom-left-radius: 100% 50px;
  border-bottom-right-radius: 100% 50px;
  transform: translateY(-100%);
  transition: transform 0.5s ease;
  z-index: 2;
}
.category-buttons {
  display: flex;
 
  padding: 10px 0;
  justify-content: center;
}

.cta-action-btn {
  color: #fff;
  border: none;
  background: transparent;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  position: relative;
}

.cta-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 
    4px 4px 8px rgba(0, 0, 0, 0.2),
    -2px -2px 4px rgba(255, 255, 255, 0.1),
    inset 1px 1px 2px rgba(255, 255, 255, 0.2),
    inset -1px -1px 2px rgba(0, 0, 0, 0.1);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(0, 0, 0, 0.05));
}

.cta-action-btn:active {
  transform: translateY(0px);
  box-shadow: 
    2px 2px 4px rgba(0, 0, 0, 0.15),
    -1px -1px 2px rgba(255, 255, 255, 0.08),
    inset 2px 2px 4px rgba(0, 0, 0, 0.15),
    inset -1px -1px 2px rgba(255, 255, 255, 0.1);
  background: linear-gradient(145deg, rgba(0, 0, 0, 0.08), rgba(255, 255, 255, 0.05));
}

.cta-pipe-separator {
  color: #fff;
  opacity: 0.5;
  font-weight: 300;
  font-size: 14px;
  margin: 0 4px;
  display: flex;
  align-items: center;
  user-select: none;
}


 


/* Bottom pink bar */
.pink-bottom-bar {
  position: absolute;
  bottom: 0px; 
  left: 0;
  width: 100%;
  height: 50px;
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  display: flex;
  justify-content: center;
  align-items: center;
  transform: translateY(100%);
  transition: transform 0.4s ease;
  z-index: 2;
}

/* Social icons inside bottom bar */

 
.latest-apps-section {
  background: #fff;
 
}

.pink-bottom-bar .social-icons li a {
  color: white;
  font-size: 16px;
  transition: color 0.3s ease;
}

.pink-bottom-bar .social-icons li a:hover {
  color: #ffc1e3; /* lighter pink */
}

/* Hover effect to slide in shapes */
.single-blog-post:hover .pink-top-shape,
.single-blog-post:active .pink-top-shape {
  transform: translateY(0);
}

.single-blog-post:hover .pink-bottom-bar,
.single-blog-post:active .pink-bottom-bar {
  transform: translateY(0);
}

/* Zoom in effect on card hover and active (for mobile touch) - doesn't affect other cards */
.single-blog-post-model:hover,
.single-blog-post-model:active {
  transform: scale(1.016);
  z-index: 10;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1);
}

/* Card Link Wrapper - Makes entire card clickable */
.card-link-wrapper {
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  cursor: pointer;
}

.card-link-wrapper:hover {
  text-decoration: none;
}

/* Content adjustments */
.content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
}
.single-blog-post{
  position: relative;
}

.single-blog-post-model .content h3 {
  color: #e91e63 !important; /* pink color for title */
  font-weight: 600 !important;
  font-size: 16px !important;
  margin-bottom: 8px !important;
  line-height: 1.5 !important;
  min-height: 48px !important; /* 16px * 1.5 * 2 = 48px for exactly 2 lines */
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-align: left !important;
  word-wrap: break-word !important;
  word-break: break-word !important;
  width: 100% !important;
  max-width: 100% !important;
  white-space: normal !important;
  text-overflow: ellipsis !important;
}
.single-blog-post .content h3 {
  color: #000000 !important; /* pink color for title */
  font-weight: 600 !important;
  font-size: 16px !important;
  margin-bottom: 8px !important;
  line-height: 1.5 !important;
  min-height: 48px !important; /* 16px * 1.5 * 2 = 48px for exactly 2 lines */
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
  text-align: left !important;
  word-wrap: break-word !important;
  word-break: break-word !important;
  width: 100% !important;
  max-width: 100% !important;
  white-space: normal !important;
  text-overflow: ellipsis !important;
}

.content h3:hover {
  text-decoration: underline;
}

.card-description {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin: 0;
  margin-top: 3px;
  min-height: 33.6px; /* 12px * 1.4 * 2 = 33.6px for exactly 2 lines */
  max-height: 33.6px; /* Prevent more than 2 lines */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}


.single-blog-post .content .meta {
  margin-bottom: 10px;
  flex-shrink: 0;
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  z-index: 998;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  padding: 0;
  margin: 0;
  justify-content: space-between;
}

.category-meta-item {
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  margin: 0;
  flex-shrink: 0;
  width: calc(50% - 14px);
  max-width: calc(50% - 14px);
  box-sizing: border-box;
}

/* Simple tag chip design */
.tag-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  flex-shrink: 0;
  width: calc(50% - 4px);
  max-width: calc(50% - 4px);
  box-sizing: border-box;
  transition: all 0.2s ease;
  cursor: default;
  position: relative;
}

.single-blog-post:hover .content ul li.tag-chip,
.single-blog-post:active .content ul li.tag-chip {
  border-color: rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: #e91e63 !important;
}

.single-blog-post:hover .content ul li.tag-chip::before,
.single-blog-post:active .content ul li.tag-chip::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  z-index: -1;
}


/* Tag above title container */
.tag-above-title {
  margin-bottom: 20px;
  text-align: left;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

/* Hide the Limited-Time Offer tag on hover to prevent overlap */
.single-blog-post:hover .tag-above-title,
.single-blog-post-model:hover .tag-above-title {
  opacity: 0;
  visibility: hidden;
}

.tag-above-title .tag-simple {
  color: rgb(0, 0, 0) !important;
  font-size: 12px;
  font-weight: 400;
}

/* Simple tag design (right side) */
.tag-simple {
  display: inline-block;
  padding: 0;
  background: transparent !important;
  border: none !important;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  margin: 0;
  box-sizing: border-box;
  transition: color 0.2s ease;
}


/* Card hover effect - white background with black text */
.single-blog-post:hover .category-meta-item,
.single-blog-post:active .category-meta-item {
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.single-blog-post:hover .category-meta-item .category-text-content,
.single-blog-post:active .category-meta-item .category-text-content,
.single-blog-post:hover .category-meta-item .category-text-content .marquee-text,
.single-blog-post:active .category-meta-item .category-text-content .marquee-text,
.single-blog-post:hover .category-meta-item i,
.single-blog-post:active .category-meta-item i {
  color: #000000 !important;
  -webkit-text-fill-color: #000000 !important;
  background: none !important;
}

.category-meta-item:hover {
  transform: translateY(-2px);
  box-shadow: 
    4px 4px 8px rgba(0, 0, 0, 0.2),
    -2px -2px 4px rgba(255, 255, 255, 0.1),
    inset 1px 1px 2px rgba(255, 255, 255, 0.2),
    inset -1px -1px 2px rgba(0, 0, 0, 0.1);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(0, 0, 0, 0.05));
}

.category-meta-item:hover .category-text-content,
.category-meta-item:hover .category-text-content .marquee-text,
.category-meta-item:hover i {
  color: #000000 !important;
  -webkit-text-fill-color: #000000 !important;
  background: none !important;
}

/* Override direct hover when card is hovered */
.single-blog-post:hover .category-meta-item:hover {
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.single-blog-post:hover .category-meta-item:hover .category-text-content,
.single-blog-post:hover .category-meta-item:hover .category-text-content .marquee-text,
.single-blog-post:hover .category-meta-item:hover i {
  color: #000000 !important;
  -webkit-text-fill-color: #000000 !important;
  background: none !important;
}

.category-meta-item:active {
  transform: translateY(0px);
  box-shadow: 
    2px 2px 4px rgba(0, 0, 0, 0.15),
    -1px -1px 2px rgba(255, 255, 255, 0.08),
    inset 2px 2px 4px rgba(0, 0, 0, 0.15),
    inset -1px -1px 2px rgba(255, 255, 255, 0.1);
  background: linear-gradient(145deg, rgba(0, 0, 0, 0.08), rgba(255, 255, 255, 0.05));
}

.category-text-content {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Remove performance-heavy JS-calculated marquee in favor of clean ellipsis */
.category-text-content.needs-marquee {
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-text-content .marquee-text {
  display: inline;
  white-space: nowrap;
}

.category-text-content.needs-marquee:hover .marquee-text {
  animation-play-state: paused;
}



.single-app-card {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 16px;
  transition: box-shadow 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px;
  background: linear-gradient(279.99deg, #9F5FF196 -1.19%, #FF54B042 50.99%, #FF9F5A69 100%);
  text-align: center;
}


.single-app-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
}

.check-related {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row-reverse;
  height: 100%;
}

.check-related img {
  height: 220px !important;
  max-height: 220px;
  object-fit: contain;
}

.phone {
  width: 160px;
  position: relative;
}

.phone img {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}

.phone-left {
  z-index: 1;
  margin-left: -75px;
}

.phone.phone-left img {
  height: 100% !important;
}

.phone.phone-right {
  transform: translateY(19px);
}

/* Load More Button Styles */
.load-more-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  margin-top: 20px;
}

.load-more-btn {
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
  border: none;
  border-radius: 50px;
  padding: 14px 40px;
  color: #fff;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 15px rgba(237, 39, 117, 0.4);
  position: relative;
  overflow: hidden;
}

.load-more-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.load-more-btn:hover::before {
  left: 100%;
}

.load-more-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(237, 39, 117, 0.5);
  background: linear-gradient(44.44deg, #FF7448 7.79%, #ED2775 94.18%);
}

.load-more-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(237, 39, 117, 0.4);
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.load-more-btn:disabled:hover {
  transform: none;
  box-shadow: 0 4px 15px rgba(237, 39, 117, 0.4);
}

/* Facebook-style Skeleton Card Loaders */
@keyframes skeleton-loading {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.skeleton-image, .skeleton-category, .skeleton-title, .skeleton-text, .skeleton-title-short {
  position: relative;
  overflow: hidden;
  background: #e0e0e0;
  border-radius: 8px;
}

.skeleton-image::after, .skeleton-category::after, .skeleton-title::after, .skeleton-text::after, .skeleton-title-short::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: skeleton-loading 1.2s infinite;
}

.skeleton-image {
  width: 100%;
  height: 220px;
  margin-bottom: 20px;
}

.skeleton-category {
  width: 40%;
  height: 24px;
  margin-bottom: 12px;
}

.skeleton-title {
  width: 90%;
  height: 20px;
  margin-bottom: 12px;
}

.skeleton-title-short {
  width: 60%;
  height: 20px;
  margin-bottom: 20px;
}
.skeleton-description, .skeleton-chip {
  position: relative;
  overflow: hidden;
  background: #e0e0e0;
  border-radius: 4px;
}

.skeleton-description::after, .skeleton-chip::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: skeleton-loading 1.2s infinite;
}

.skeleton-description {
  width: 100%;
  height: 36px;
  margin-top: 4px;
}

.skeleton-chips {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.skeleton-chip {
  width: 70px;
  height: 28px;
  border-radius: 25px;
}

.skeleton-chip:nth-child(2) {
  width: 100px;
}
@keyframes shimmer-move {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@media (max-width: 767px) {
  .load-more-btn {
    padding: 12px 30px;
    font-size: 14px;
  }
  
  .load-more-container {
    padding: 30px 15px;
  }
  
  .skeleton-card {
    padding: 30px 15px 20px;
    width: 100%;
    background: #f0f0f0;
  }
  
  .skeleton-image {
    height: 180px;
    margin-bottom: 15px;
  }
  
  .skeleton-title,
  .skeleton-title-short {
    height: 18px;
  }
  
  .skeleton-description {
    height: 32px;
  }
  
  .skeleton-chip {
    width: 60px;
    height: 24px;
  }
}

/* ==========================================
   GEO OPTIMIZATION & CAPABILITIES MATRIX
   ========================================== */
.geo-capabilities-section {
  padding: 80px 20px;
  background: radial-gradient(circle at 10% 20%, rgba(68, 0, 178, 0.05) 0%, rgba(237, 39, 117, 0.02) 90%);
  position: relative;
  overflow: hidden;
  border-top: 1px solid rgba(159, 95, 241, 0.1);
  font-family: 'Outfit', 'Inter', sans-serif;
}

.geo-capabilities-section::before {
  content: '';
  position: absolute;
  top: -150px;
  right: -150px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(237, 39, 117, 0.08) 0%, transparent 70%);
  filter: blur(50px);
  pointer-events: none;
}

.geo-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 80px;
}

.geo-section-wrapper {
  position: relative;
  z-index: 2;
}

/* Badge Tags & Pulsing AI Indicator */
.badge-tag {
  display: inline-block;
  padding: 4px 14px;
  background: rgba(139, 79, 235, 0.1);
  color: #8b4feb;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 15px;
  border: 1px solid rgba(139, 79, 235, 0.15);
}

.badge-ai-pulsing {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 14px;
  background: linear-gradient(135deg, rgba(237, 39, 117, 0.15) 0%, rgba(255, 116, 72, 0.15) 100%);
  color: #ED2775;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 15px;
  border: 1px solid rgba(237, 39, 117, 0.25);
  box-shadow: 0 4px 10px rgba(237, 39, 117, 0.1);
}

.pulse-dot {
  width: 7px;
  height: 7px;
  background-color: #ED2775;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  animation: pulse-ring 1.8s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(237, 39, 117, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 8px rgba(237, 39, 117, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(237, 39, 117, 0);
  }
}

/* Titles */
.geo-section-title {
  font-size: 36px;
  font-weight: 800;
  line-height: 1.2;
  color: #1e1145;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
}

.geo-section-desc {
  font-size: 18px;
  line-height: 1.6;
  color: #554a70;
}

.geo-section-subtitle {
  font-size: 16px;
  color: #695e86;
  max-width: 600px;
  margin: 0 auto 40px;
}

.section-header {
  margin-bottom: 40px;
}

/* 1. Who We Are Card (Glassmorphism & Neon Glow) */
.who-we-are-card {
  position: relative;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 24px;
  padding: 50px 40px;
  box-shadow: 0 20px 40px rgba(68, 0, 178, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  overflow: hidden;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}

.who-we-are-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(ellipse at center, rgba(139, 79, 235, 0.06) 0%, transparent 60%);
  pointer-events: none;
  animation: rotateGlow 25s linear infinite;
}

@keyframes rotateGlow {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.who-we-are-card .card-meta {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}

.who-we-are-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 40px;
  border-top: 1px solid rgba(139, 79, 235, 0.1);
  padding-top: 30px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 140px;
}

.stat-val {
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, #ED2775 0%, #8B4FEB 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 5px;
}

.stat-lbl {
  font-size: 12px;
  font-weight: 600;
  color: #7d729c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 2. What We Do Grid (Responsive CSS Grid & Premium Hover Card) */
.geo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 30px;
}

.service-card {
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 25px rgba(68, 0, 178, 0.03);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.service-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 35px rgba(68, 0, 178, 0.08), 0 0 1px rgba(139, 79, 235, 0.3);
  border-color: rgba(139, 79, 235, 0.2);
  background: rgba(255, 255, 255, 0.95);
}

.service-icon {
  width: 70px;
  height: 70px;
  border-radius: 50% 50% 50% 0; /* Premium droplet shape pointed at bottom-left */
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.service-card:hover .service-icon {
  transform: rotate(180deg);
}

/* Alternate sequential colors for droplet icons to match IncludedBestFeatures.vue style */
/* Card 1 */
.service-card:nth-child(6n+1) .service-icon,
.service-card:nth-of-type(6n+1) .service-icon {
  background: linear-gradient(45.24deg, #9F5FF1 5.99%, #FF54B0 91.87%);
}
/* Card 2 */
.service-card:nth-child(6n+2) .service-icon,
.service-card:nth-of-type(6n+2) .service-icon {
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
}
/* Card 3 */
.service-card:nth-child(6n+3) .service-icon,
.service-card:nth-of-type(6n+3) .service-icon {
  background: linear-gradient(44.44deg, #3F75FF 7.79%, #4ADEFF 94.18%);
}
/* Card 4 */
.service-card:nth-child(6n+4) .service-icon,
.service-card:nth-of-type(6n+4) .service-icon {
  background: linear-gradient(44.44deg, #FF4B3F 7.79%, #FFAC30 94.18%);
}
/* Card 5 */
.service-card:nth-child(6n+5) .service-icon,
.service-card:nth-of-type(6n+5) .service-icon {
  background: linear-gradient(45.24deg, #9F5FF1 5.99%, #FF54B0 91.87%);
}
/* Card 6 */
.service-card:nth-child(6n+6) .service-icon,
.service-card:nth-of-type(6n+6) .service-icon {
  background: linear-gradient(44.44deg, #ED2775 7.79%, #FF7448 94.18%);
}

/* Ensure the child icon size is centered and matches */
.service-icon svg {
  width: 32px;
  height: 32px;
  position: static !important;
  transform: none !important;
}

.service-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e1145;
  margin-bottom: 12px;
  letter-spacing: -0.2px;
}

.service-desc {
  font-size: 14px;
  line-height: 1.5;
  color: #554a70;
  margin: 0;
}

/* 3. Who We Help Cards (Responsive Grid Layout) */
.industry-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
  max-width: 1200px;
  margin: 0 auto;
}

.industry-card {
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 20px;
  padding: 35px 24px;
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  z-index: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
}

.industry-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #ff3d77 0%, #ff7448 100%);
  opacity: 0;
  z-index: -1;
  transition: opacity 0.4s ease;
}

.industry-card:hover::before {
  opacity: 1;
}

.industry-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 35px rgba(255, 61, 119, 0.25);
  border-color: transparent;
  background: transparent;
}

.industry-card:hover .industry-title,
.industry-card:hover .industry-desc {
  color: #ffffff !important;
}

.industry-card:hover .industry-icon {
  transform: rotate(180deg);
}

.industry-icon {
  width: 65px;
  height: 65px;
  border-radius: 50% 50% 50% 0; /* Premium droplet shape pointed at bottom-left */
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  color: #ffffff !important;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

/* Sequential dynamic gradients for industry icons */
.industry-card:nth-child(even) .industry-icon {
  background: linear-gradient(135deg, #ff3d77 0%, #ff7448 100%) !important;
}

.industry-card:nth-child(odd) .industry-icon {
  background: linear-gradient(135deg, #51C1FD 0%, #7d6cf6 100%) !important;
}

.industry-icon svg {
  width: 26px;
  height: 26px;
  stroke: #ffffff !important;
  fill: none;
}

.industry-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
  align-items: center;
}

.industry-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e1145;
  margin: 0;
  letter-spacing: -0.2px;
  transition: color 0.3s ease;
}

.industry-desc {
  font-size: 13px;
  line-height: 1.5;
  color: #554a70;
  margin: 0;
  transition: color 0.3s ease;
}

/* Responsive GEO Adjustments */
@media (max-width: 768px) {
  .geo-capabilities-section {
    padding: 60px 15px;
  }
  
  .geo-container {
    gap: 60px;
  }
  
  .geo-section-title {
    font-size: 28px;
  }
  
  .who-we-are-card {
    padding: 35px 20px;
  }
  
  .who-we-are-stats {
    gap: 20px;
  }
  
  .stat-item {
    min-width: 100px;
  }
  
  .stat-val {
    font-size: 26px;
  }
  
  .geo-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .service-card {
    display: flex !important;
    flex-direction: row !important;
    align-items: flex-start !important;
    text-align: left !important;
    padding: 24px 20px !important;
    gap: 18px !important;
  }
  
  .service-icon {
    margin: 0 0 0 0 !important;
    flex-shrink: 0 !important;
    width: 55px !important;
    height: 55px !important;
  }
  
  .service-content {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    text-align: left !important;
    flex: 1;
  }

  .service-title {
    text-align: left !important;
    margin-top: 0 !important;
    margin-bottom: 6px !important;
    font-size: 16px !important;
  }
  
  .service-desc {
    text-align: left !important;
    font-size: 13px !important;
    line-height: 1.5 !important;
  }
  
  .industry-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .industry-card {
    display: flex !important;
    flex-direction: row !important;
    align-items: flex-start !important;
    text-align: left !important;
    padding: 24px 20px !important;
    gap: 18px !important;
  }
  
  .industry-icon {
    margin: 0 0 0 0 !important;
    flex-shrink: 0 !important;
    width: 55px !important;
    height: 55px !important;
  }
  
  .industry-content {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    text-align: left !important;
    flex: 1;
  }
  
  .industry-title {
    text-align: left !important;
    margin-top: 0 !important;
    margin-bottom: 6px !important;
    font-size: 16px !important;
  }
  
  .industry-desc {
    text-align: left !important;
    font-size: 13px !important;
    line-height: 1.5 !important;
  }
}

/* Removed fade-in-card animation to prevent delay after skeleton loader */
/* Cards now appear immediately after skeleton is removed */

/* WHO WE ARE CTA BUTTON STYLES */
.who-we-are-cta-container {
  margin: 25px auto 10px auto;
  display: flex;
  justify-content: center;
}

.who-we-are-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #8B4FEB 0%, #ED2775 100%);
  color: #ffffff !important;
  font-weight: 700;
  font-size: 15px;
  padding: 14px 28px;
  border-radius: 30px;
  box-shadow: 0 8px 25px rgba(237, 39, 117, 0.25);
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  text-decoration: none !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
}

.who-we-are-cta-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(237, 39, 117, 0.4);
  background: linear-gradient(135deg, #ED2775 0%, #FF7448 100%);
}

.who-we-are-cta-btn:active {
  transform: translateY(-1px);
}

.cta-arrow {
  transition: transform 0.3s ease;
}

.who-we-are-cta-btn:hover .cta-arrow {
  transform: translateX(5px);
}


</style>
<script>
import { defineAsyncComponent } from 'vue';
import axios from "axios";
import Navbar from "../Layout/Navbar";
const FooterStyleFour = defineAsyncComponent(() => import("../Layout/FooterStyleFour"));
const FormModal = defineAsyncComponent(() => import("../HomeThree/FormModal.vue"));
import { injectSchemaMarkup, generateWebSiteSchema, generateOrganizationSchema, generateFAQSchema } from "../../utils/schemaUtils";

import { Search_APP, GET_APPS_LIST, GET_CATEGORIES } from '../../services/graphqlService';
import apolloClient from '../../apollo';
import { currencyStore } from "@/store/currencyStore";

export default {
  name: "TemplatesAndThemes",
  components: {
    Navbar,
    FooterStyleFour,
    FormModal
  },
  data() {
    return {
      appDATA: [],
      allApps: [], // Store all fetched apps
      filteredApps: [],
      showPopup: false,
      displayedItemsCount: 8, // Show 8 initially
      pageSize: 8, // Batch size for loading more
      loading: true,
      loadingMore: false,
      skeletonCount: 8, // Match pageSize to 8
      errorMessage: "",
      searchQuery: "",
      showSearchResults: false,
      hideSearchTimeout: null,
      categoryMarqueeCache: {},
      selectedCategory: null,
      totalItems: 0, // Store total count from server
      searchDebounceTimeout: null,
      activeFilters: {}, // Store filters for pagination
      animatedPlaceholder: "",
      pauseTyping: false,
      typingInterval: null,
      placeholderTexts: [
        "Find what you need quickly...",
        "Grocery Delivery App...",
        "Food Ordering App...",
        "Fitness Monitoring App..."
      ],
      currentPlaceholderIndex: 0,
      currentCharIndex: 0,
      isDeleting: false,
      availableCategories: [
        {
          name: "Trending",
          slug: "trending"
        },
        {
          name: "Products",
          slug: "products"
        },
        {
          name: "Integration",
          slug: "integraiton"
        },
        {
          name: "Services",
          slug: "services"
        },
        {
          name: "Capstone",
          slug: "capstone"
        },
        {
          name: "Automations",
          slug: "automations"
        }
      ],
      scrollListener: null,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      homepageFaqs: [
        {
          question: "What custom software development services does Meander Software provide?",
          answer: "We specialize in custom mobile app development (native and hybrid for iOS & Android), secure and scalable multi-tenant SaaS platforms, enterprise systems, DevOps automation, and secure cloud engineering across AWS, Google Cloud, and Azure."
        },
        {
          question: "How does Meander Software use artificial intelligence in software applications?",
          answer: "We integrate Large Language Models (LLMs), machine learning models, and custom AI agents into our software applications. This helps businesses automate manual tasks, analyze data, and build smarter workflows."
        },
        {
          question: "Which industries and business sizes does Meander Software support?",
          answer: "We build scalable, custom solutions for startups (MVPs and prototypes), global enterprises (legacy integrations and modern architectures), SMBs, and sectors like healthcare, logistics, fintech, education, and e-commerce."
        },
        {
          question: "Where is Meander Software located?",
          answer: "Our primary development operations are in India, and we serve a diverse client base globally."
        },
        {
          question: "How can I request a quote or get in touch about a software project?",
          answer: "You can easily schedule a consultation by booking a call directly on our website or by reaching out to our team at info@meander.software. We'll discuss your specific requirements to provide a custom project quote."
        }
      ],
    };
  },
  computed: {
    hasMoreItems() {
      // Use the total count from the server to determine if more items exist
      return this.totalItems > this.displayedItemsCount;
    },
    shouldShowLoadMoreButton() {
      // Show button whenever more items are available, preventing scroll auto-load
      return this.hasMoreItems;
    },
    isIndianUser() {
      return currencyStore.isIndianUser;
    },
    countryDetected() {
      return currencyStore.countryDetected;
    },
    currentPage() {
      // Calculate current page based on displayedItemsCount
      // page 1 = 16 items, page 2 = 32 items, page 3 = 48 items, etc.
      return Math.ceil(this.displayedItemsCount / this.pageSize);
    },
  },
  methods: {
    // Popup control
    openPopup() {
      this.showPopup = true;
    },
    closePopup() {
      this.showPopup = false;
    },
    handleCtaClick(cta) {
      // Check if link exists and is not empty
      if (cta.link && cta.link.trim() !== '') {
        const link = cta.link.trim();
        
        // Check if it's an external link (starts with http:// or https://)
        if (link.startsWith('http://') || link.startsWith('https://')) {
          // Open external link in new tab
          window.open(link, '_blank', 'noopener,noreferrer');
        } else {
          // Internal link - use router navigation
          // Check if it's an absolute path or relative
          if (link.startsWith('/')) {
            this.$router.push(link);
          } else {
            // Relative path
            this.$router.push(`/${link}`);
          }
        }
      } else {
        // No link, open popup
        this.openPopup();
      }
    },
    isInternalLink(link) {
      if (!link) return false;
      // Check if link is internal (starts with /) or external (starts with http:// or https://)
      return link.startsWith('/') && !link.startsWith('//');
    },
    openCalendly() {
      // eslint-disable-next-line no-undef
      if (typeof Calendly !== 'undefined') {
        Calendly.initPopupWidget({url:'https://calendly.com/connect-to-meander/30min'});
      }
      return false;
    },
    navigateToDetail(slug) {
      this.$router.push(`/project/${slug}`);
    },
  needsMarquee(id, text) {
    if (!text) return false;
    // Check cache first
    if (this.categoryMarqueeCache[id] !== undefined) {
      return this.categoryMarqueeCache[id];
    }
    // Estimate based on text length (rough estimate: ~8px per character)
    // This is a quick check without DOM measurement
    const estimatedWidth = text.length * 8;
    const needsMarquee = estimatedWidth > 140;
    this.categoryMarqueeCache[id] = needsMarquee;
    return needsMarquee;
  },
    checkCategoryMarquee() {
      // Optimized: Use CSS-only approach to avoid forced reflow from scrollWidth
      return;
    },
    async getAppQuery() {
      this.loading = true;
      this.errorMessage = "";
      try {
        // Get type from URL query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const typeFromUrl = urlParams.get('type');
        const categoryFromUrl = urlParams.get('category');
        
        // Build GraphQL filters object
        const filters = {};
        
        // Add type filter only if type is provided in URL
        if (typeFromUrl) {
          // Special handling for "Plug n Play" type - use boolean field instead
          if (typeFromUrl.toLowerCase() === 'plug n play') {
            filters.plugNPlay = { eq: true };
          } else {
            // For other types (Integration, Model, etc.) use the type field
            filters.type = { eq: typeFromUrl };
          }
        }
        
        // Add category filter if category is selected
        if (this.selectedCategory || categoryFromUrl) {
          const categorySlug = this.selectedCategory || categoryFromUrl;
          let querySlugs = [categorySlug];
          
          if (categorySlug === 'trending' || categorySlug === 'trending-apps') {
            querySlugs = ['trending', 'trending-apps'];
          } else if (categorySlug === 'products' || categorySlug === 'popular-apps') {
            querySlugs = ['products', 'popular-apps'];
          } else if (categorySlug === 'integraiton' || categorySlug === 'integration' || categorySlug === 'on-demand-apps') {
            querySlugs = ['integraiton', 'integration', 'on-demand-apps'];
          } else if (categorySlug === 'automations' || categorySlug === 'business-apps') {
            querySlugs = ['automations', 'business-apps'];
          }
          
          // Support filtering by both categories and tags collections using OR logical operator
          filters.or = [
            { categorories: { slug: { in: querySlugs } } },
            { tags: { slug: { in: querySlugs } } }
          ];
        }
        
        // Always filter by is_visible for performance and correctness
        filters.is_visible = { eq: true };
        this.activeFilters = { ...filters }; // Store for loadMore
        
        // Fetch a larger buffer (100) to support seamless scrolling/paging
        // Reduced to pageSize to improve initial load performance
        const pagination = {
          page: 1,
          pageSize: this.pageSize
        };
        
        // Execute GraphQL query with lean GET_APPS_LIST
        const { data } = await apolloClient.query({
          query: GET_APPS_LIST,
          variables: {
            filters: filters,
            pagination: pagination,
            sort: ["isFeatured:desc", "Priorities:desc"]
          }
        });
        
        // Transform GraphQL response
        let allApps = (data.apps || []).map(app => ({
          ...app,
          id: app.documentId || app.id,
          isFeatured: app.isFeatured === true || app.isFeatured === 'true' || false,
          Priorities: app.Priorities !== null && app.Priorities !== undefined 
            ? Number(app.Priorities) 
            : 0
        }));
        
        // Store total items from connection for pagination logic
        this.totalItems = data.apps_connection?.pageInfo?.total || allApps.length;
        
        // Store all apps (now already sorted by server)
        this.allApps = allApps;
        
        // Apply slicing based on displayedItemsCount
        const displayedApps = allApps.slice(0, this.displayedItemsCount);
        
        this.appDATA = {
          data: displayedApps,
          meta: {
            pagination: {
              total: allApps.length
            }
          }
        };
      } catch (error) {
        console.error(error);
        this.errorMessage =
          "There was a problem loading the data. Please try again later.";
      } finally {
        this.loading = false;
      }
    },
    async fetchCategories() {
      try {
        // Fetch categories directly using lean GraphQL query targeting tags
        const { data } = await apolloClient.query({
          query: GET_CATEGORIES
        });
        
        let allCategories = data.tags || data.businessModelCategories || [];
        const order = ["trending", "products", "integraiton", "services", "capstone", "automations"];
        
        // Map fetched categories to new names/slugs
        const mapped = allCategories.map(cat => {
          let slug = cat.slug;
          let name = cat.name;
          
          if (slug === 'trending-apps' || slug === 'trending') {
            slug = 'trending';
            name = 'Trending';
          } else if (slug === 'popular-apps' || slug === 'products') {
            slug = 'products';
            name = 'Products';
          } else if (slug === 'on-demand-apps' || slug === 'integration' || slug === 'integraiton') {
            slug = 'integraiton';
            name = 'Integration';
          } else if (slug === 'business-apps' || slug === 'automations') {
            slug = 'automations';
            name = 'Automations';
          } else if (slug === 'services') {
            slug = 'services';
            name = 'Services';
          } else if (slug === 'capstone') {
            slug = 'capstone';
            name = 'Capstone';
          }
          
          return {
            name,
            slug,
            documentId: cat.documentId
          };
        });

        // Unique filter to avoid duplicates, allowing all slugs dynamically (e.g. toolkit)
        const seen = new Set();
        const uniqueMapped = mapped.filter(cat => {
          if (!cat.slug) return false;
          if (seen.has(cat.slug)) return false;
          seen.add(cat.slug);
          return true; // Allow toolkit and all other dynamic tags
        });

        // Use mapped list if anything is returned, otherwise keep the default static list
        if (uniqueMapped.length > 0) {
          const defaultCategories = [
            { name: "Trending", slug: "trending" },
            { name: "Products", slug: "products" },
            { name: "Integration", slug: "integraiton" },
            { name: "Services", slug: "services" },
            { name: "Capstone", slug: "capstone" },
            { name: "Automations", slug: "automations" }
          ];

          const merged = [...defaultCategories];
          uniqueMapped.forEach(cat => {
            const existing = merged.find(m => m.slug === cat.slug);
            if (existing) {
              existing.documentId = cat.documentId;
              if (cat.name) existing.name = cat.name;
            } else {
              merged.push(cat);
            }
          });

          this.availableCategories = merged.sort((a, b) => {
            const indexA = order.indexOf(a.slug);
            const indexB = order.indexOf(b.slug);
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return a.name.localeCompare(b.name); // Alphabetical sort for dynamic categories like toolkit
          });
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },
    filterByCategory(categorySlug) {
      this.selectedCategory = categorySlug;
      this.displayedItemsCount = 8; // Reset to initial count when filtering
      
      // Update URL query parameter
      const query = { ...this.$route.query };
      if (categorySlug) {
        query.category = categorySlug;
      } else {
        delete query.category;
      }
      delete query.page; // Remove page query param
      
      this.$router.push({
        path: this.$route.path,
        query: query
      });
      
      // Fetch filtered data
      this.getAppQuery();
    },
    updateUrlPage() {
      // Save current scroll position before URL update
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      
      // Update URL with current page number
      const query = { ...this.$route.query };
      const newPage = this.currentPage;
      
      if (newPage > 1) {
        query.page = newPage.toString();
      } else {
        delete query.page;
      }
      
      // Update URL without triggering navigation
      this.$router.replace({
        path: this.$route.path,
        query: query
      });
      
      // Restore scroll position multiple times to override router scroll behavior
      const restoreScroll = () => {
        window.scrollTo(0, scrollPosition);
      };
      
      // Restore immediately and in next frames to ensure it sticks
      this.$nextTick(() => {
        restoreScroll();
        requestAnimationFrame(() => {
          restoreScroll();
          requestAnimationFrame(() => {
            restoreScroll();
          });
        });
      });
    },
    async loadMore(isFromButton = false) {
      if (this.loadingMore || !this.hasMoreItems) return;
      
      // Calculate how many items are left in the local buffer
      let remainingItems = this.allApps.length - this.displayedItemsCount;
      
      // If no items left in local buffer, fetch next page from server
      if (remainingItems <= 0) {
        this.loadingMore = true;
        try {
          // Calculate the next page based on total fetched apps and pageSize
          const nextPage = Math.floor(this.allApps.length / this.pageSize) + 1;
          const { data } = await apolloClient.query({
            query: GET_APPS_LIST,
            variables: {
              filters: this.activeFilters,
              pagination: { page: nextPage, pageSize: this.pageSize },
              sort: ["isFeatured:desc", "Priorities:desc"]
            }
          });
          
          if (data?.apps?.length) {
            const newApps = (data.apps).map(app => ({
              ...app,
              id: app.documentId || app.id,
              isFeatured: app.isFeatured === true || app.isFeatured === 'true' || false,
              Priorities: app.Priorities !== null && app.Priorities !== undefined 
                ? Number(app.Priorities) 
                : 0
            }));
            this.allApps = [...this.allApps, ...newApps];
            remainingItems = newApps.length;
          } else {
            this.loadingMore = false;
            return;
          }
        } catch (error) {
          console.error("Error loading more apps from server:", error);
          this.loadingMore = false;
          return;
        }
      }

      const itemsToLoad = remainingItems < this.pageSize ? remainingItems : this.pageSize;
      
      // Set skeleton count to match items being loaded
      this.skeletonCount = itemsToLoad;
      this.loadingMore = true;
      
      // Add delay to show skeleton loaders (minimum 800ms)
      setTimeout(() => {
        
        // Increase displayed items count
        this.displayedItemsCount += itemsToLoad;
        
        // Update displayed apps
        const displayedApps = this.allApps.slice(0, this.displayedItemsCount);
        this.appDATA = {
          data: displayedApps,
          meta: {
            pagination: {
              total: this.allApps.length
            }
          }
        };
        
        // Update URL with new page number
        this.updateUrlPage();
        
        // Check marquee after new items are loaded
        this.$nextTick(() => {
          setTimeout(() => {
            this.checkCategoryMarquee();
          }, 150);
        });
        
        this.loadingMore = false;
      }, 500);
    },
    handleScroll() {
      // Don't auto-load if we should show the button instead
      if (this.shouldShowLoadMoreButton) return;
      
      // Check if user has scrolled near the bottom (within 200px)
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // If scrolled near bottom and there are more items, auto-load
      if (scrollTop + windowHeight >= documentHeight - 200 && this.hasMoreItems && !this.loadingMore) {
        this.loadMore();
      }
    },
    // Search functionality methods
    async handleSearch() {
      clearTimeout(this.hideSearchTimeout);
      
      if (this.searchQuery.trim() === '') {
        this.filteredApps = [];
        this.showSearchResults = false;
        this.pauseTyping = false;
        return;
      }

      this.pauseTyping = true;
      
      // Debounce the actual API call
      if (this.searchDebounceTimeout) clearTimeout(this.searchDebounceTimeout);
      
      this.searchDebounceTimeout = setTimeout(async () => {
        try {
          const query = this.searchQuery.toLowerCase();
          const variables = {
            filters: {
              or: [
                { clone_synonyms: { containsi: query } },
                { alias_synonyms: { containsi: query } },
                { keywords: { containsi: query } },
                { name: { containsi: query } },
                { title: { containsi: query } },
                { description: { containsi: query } },
                { one_liner: { containsi: query } }
              ]
            },
            pagination: { page: 1, pageSize: 20 },
            sort: ["isFeatured:desc", "Priorities:desc"]
          };
          
          this.showSearchResults = true;
          
          const { data } = await apolloClient.query({
            query: Search_APP,
            variables
          });
          
          this.filteredApps = (data?.apps || []).map(app => ({
            ...app,
            id: app.documentId || app.id
          }));
        } catch (error) {
          console.error("Error searching apps:", error);
          this.filteredApps = [];
        }
      }, 300); // 300ms debounce
    },
    handleFocus() {
      clearTimeout(this.hideSearchTimeout);
      this.pauseTyping = true;
      // Show dropdown if there are filtered apps
      if (this.filteredApps.length > 0 && this.searchQuery.trim() !== '') {
        this.showSearchResults = true;
      }
    },
    handleBlur() {
      // Delay hiding results to allow click events to fire
      this.hideSearchTimeout = setTimeout(() => {
        this.showSearchResults = false;
        if (this.searchQuery.trim() === '') {
          this.pauseTyping = false;
        }
      }, 200);
    },
    navigateToApp(slug) {
      this.showSearchResults = false;
      this.searchQuery = '';
      this.$router.push(`/project/${slug}`);
    },
    startTypingAnimation() {
      if (this.typingInterval) {
        clearInterval(this.typingInterval);
      }
      
      // Initialize with first character
      const firstText = this.placeholderTexts[this.currentPlaceholderIndex];
      this.animatedPlaceholder = firstText.substring(0, 1);
      this.currentCharIndex = 1;
      
      const animate = () => {
        if (this.pauseTyping) {
          return;
        }
        
        const currentText = this.placeholderTexts[this.currentPlaceholderIndex];
        
        if (!this.isDeleting) {
          // Typing
          if (this.currentCharIndex < currentText.length) {
            this.animatedPlaceholder = currentText.substring(0, this.currentCharIndex + 1);
            this.currentCharIndex++;
          } else {
            // Finished typing, wait before deleting
            this.isDeleting = true;
            // Clear interval and restart after delay
            clearInterval(this.typingInterval);
            setTimeout(() => {
              this.typingInterval = setInterval(animate, 50); // Faster deletion
            }, 200);
          }
        } else {
          // Deleting
          if (this.currentCharIndex > 0) {
            this.animatedPlaceholder = currentText.substring(0, this.currentCharIndex - 1);
            this.currentCharIndex--;
          } else {
            // Finished deleting, move to next text
            this.isDeleting = false;
            this.currentPlaceholderIndex = (this.currentPlaceholderIndex + 1) % this.placeholderTexts.length;
            this.currentCharIndex = 0;
            // Clear interval and restart after delay
            clearInterval(this.typingInterval);
            setTimeout(() => {
              this.typingInterval = setInterval(animate, 50); // Normal typing speed
            }, 500);
          }
        }
      };
      
      // Start animation
      this.typingInterval = setInterval(animate, 100);
    },
    getCardPrice(data) {
      // Safely check if pricing_plan exists and has data
      if (data?.pricing_plan &&
          Array.isArray(data.pricing_plan) &&
          data.pricing_plan.length > 0 &&
          data.pricing_plan[0]?.sections &&
          Array.isArray(data.pricing_plan[0].sections) &&
          data.pricing_plan[0].sections.length > 0) {
        const section = data.pricing_plan[0].sections[0];
        
        const priceINR = section?.price;
        const priceUSD = section?.price_in_Doller;
        const hasINR = priceINR != null && priceINR !== '';
        const hasUSD = priceUSD != null && priceUSD !== '';

        // If backend sends ONLY ONE field, bypass location and use that value
        if (hasINR && !hasUSD) return priceINR;
        if (hasUSD && !hasINR) return priceUSD;

        // If BOTH or NEITHER exist, fallback to location logic
        if (this.isIndianUser) {
          return priceINR || null;
        } else {
          return priceUSD || null;
        }
      }
      return null;
    },
    getCardCurrency(data) {
      if (data?.pricing_plan &&
          Array.isArray(data.pricing_plan) &&
          data.pricing_plan.length > 0 &&
          data.pricing_plan[0]?.sections &&
          Array.isArray(data.pricing_plan[0].sections) &&
          data.pricing_plan[0].sections.length > 0) {
        const section = data.pricing_plan[0].sections[0];
        
        const priceINR = section?.price;
        const priceUSD = section?.price_in_Doller;
        const hasINR = priceINR != null && priceINR !== '';
        const hasUSD = priceUSD != null && priceUSD !== '';

        // If backend sends ONLY ONE field, return its corresponding symbol
        if (hasINR && !hasUSD) return '₹';
        if (hasUSD && !hasINR) return '$';
      }
      
      // Fallback to location logic
      return this.isIndianUser ? '₹' : '$';
    },
    getCardOffer(data) {
      // Get the Offer percentage from the first section
      if (data?.pricing_plan &&
          Array.isArray(data.pricing_plan) &&
          data.pricing_plan.length > 0 &&
          data.pricing_plan[0]?.sections &&
          Array.isArray(data.pricing_plan[0].sections) &&
          data.pricing_plan[0].sections.length > 0) {
        const section = data.pricing_plan[0].sections[0];
        return section?.Offer || null;
      }
      return null;
    },
    getOriginalPrice(data, currentPrice) {
      const offer = this.getCardOffer(data);
      if (offer && currentPrice) {
        return this.calculateOriginalPrice(currentPrice, offer);
      }
      // Fallback to 50% multiplier if no offer
      return currentPrice ? parseFloat(currentPrice) * 2 : 0;
    },
    calculateOriginalPrice(price, offerPercent) {
      if (!price || !offerPercent) return 0;
      const priceNum = parseFloat(price);
      const offerNum = parseFloat(offerPercent);
      if (isNaN(priceNum) || isNaN(offerNum)) return 0;
      if (offerNum <= 0 || offerNum >= 100) return 0;
      const original = priceNum / (1 - offerNum / 100);
      return Math.round(original);
    },
    getDisplayTags(data) {
      // Return up to 2 tags from different categories
      if (!data?.tags || !Array.isArray(data.tags) || data.tags.length === 0) {
        return [];
      }
      
      const selectedTags = [];
      const usedCategories = new Set();
      const tagsWithoutCategory = [];
      
      // First pass: Collect tags with categories and tags without categories separately
      for (const tag of data.tags) {
        if (tag?.categories && Array.isArray(tag.categories) && tag.categories.length > 0) {
          // Tag has categories - try to get one from each category
          const tagCategory = tag.categories[0]?.name;
          if (tagCategory && !usedCategories.has(tagCategory)) {
            selectedTags.push(tag);
            usedCategories.add(tagCategory);
            if (selectedTags.length >= 2) break;
          }
        } else {
          // Tag has no category, save for later
          tagsWithoutCategory.push(tag);
        }
      }
      
      // Second pass: Fill remaining slots with tags without categories or any remaining tags
      if (selectedTags.length < 2) {
        // First try tags without categories
        for (const tag of tagsWithoutCategory) {
          if (selectedTags.length >= 2) break;
          if (!selectedTags.find(t => t.name === tag.name)) {
            selectedTags.push(tag);
          }
        }
        
        // If still need more, get any remaining tags (even if same category)
        if (selectedTags.length < 2) {
          for (const tag of data.tags) {
            if (selectedTags.length >= 2) break;
            // Skip if already selected
            if (!selectedTags.find(t => t.name === tag.name)) {
              selectedTags.push(tag);
            }
          }
        }
      }
      
      return selectedTags.slice(0, 2); // Ensure max 2 tags
    },
    getAppChips(data) {
      if (data?.our_expertises && Array.isArray(data.our_expertises) && data.our_expertises.length > 0) {
        return data.our_expertises;
      }
      if (data?.chips && Array.isArray(data.chips) && data.chips.length > 0) {
        return data.chips;
      }
      if (data?.technologies && Array.isArray(data.technologies) && data.technologies.length > 0) {
        return data.technologies;
      }
      return [];
    },
    calculateDiscount(originalPrice, currentPrice) {
      if (!originalPrice || !currentPrice) return 0;
      const original = parseFloat(originalPrice);
      const current = parseFloat(currentPrice);
      if (isNaN(original) || isNaN(current)) return 0;
      if (original <= current) return 0;
      const discount = ((original - current) / original) * 100;
      return Math.round(discount);
    },
    getOptimizedImage(imageObj) {
      if (!imageObj) return '';
      const formats = imageObj.formats;
      if (formats) {
        // Since these are displayed in small cards, 'small' format (usually 500px) is sufficient 
        // for most screen sizes and loads much faster than 'medium'.
        if (formats.small) return formats.small.url;
        if (formats.medium) return formats.medium.url;
      }
      return imageObj.url;
    },
    setMetaTags() {
      // Set comprehensive SEO meta tags for homepage
      document.title = "Best Software Company in US & India | Meander Software";
      
      // Helper to set or update a meta tag
      function setMeta(name, content, attr = 'name') {
        let element = document.querySelector(`meta[${attr}="${name}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute(attr, name);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content || '');
      }

      // Set basic meta tags
      setMeta('title', 'Best Software Company in US & India | Meander Software');
      setMeta('description', 'Meander Software is a leading software development company providing professional app and website development services. We specialize in creating innovative and customized solutions to help businesses thrive in the digital world.');
      setMeta('keywords', 'software development company, app development, website development, custom software, software solutions, digital transformation, meander software');

      // Open Graph tags
      setMeta('og:title', 'Best Software Company in US & India | Meander Software', 'property');
      setMeta('og:description', 'Meander Software is a leading software development company providing professional app and website development services. We specialize in creating innovative and customized solutions.', 'property');
      setMeta('og:type', 'website', 'property');
      setMeta('og:url', window.location.href, 'property');
      setMeta('og:image', 'https://meander.software/og-default-image.jpg', 'property');
      setMeta('og:image:width', '1200', 'property');
      setMeta('og:image:height', '630', 'property');
      setMeta('og:image:alt', 'Best Software Company in US & India | Meander Software', 'property');

      // Twitter Card tags
      setMeta('twitter:title', 'Best Software Company in US & India | Meander Software', 'name');
      setMeta('twitter:description', 'Meander Software is a leading software development company providing professional app and website development services. We specialize in creating innovative and customized solutions.', 'name');
      setMeta('twitter:image', 'https://meander.software/og-default-image.jpg', 'name');
      setMeta('twitter:image:alt', 'Best Software Company in US & India | Meander Software', 'name');
      setMeta('twitter:card', 'summary_large_image', 'name');
      setMeta('twitter:site', '@meandersoftware', 'name');
      setMeta('twitter:creator', '@meandersoftware', 'name');

      // Update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', window.location.href);
      } else {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        canonical.setAttribute('href', window.location.href);
        document.head.appendChild(canonical);
      }
    },
    async detectUserCountry() {
      await currencyStore.detectUserCountry();
    },
  },
  watch: {
    // Watch the "page" query parameter in the URL
    "$route.query.page"(newPage) {
      if (newPage) {
        const page = parseInt(newPage);
        if (!isNaN(page) && page > 0) {
          const newDisplayedCount = page * this.pageSize;
          if (newDisplayedCount !== this.displayedItemsCount) {
            this.displayedItemsCount = newDisplayedCount;
            // Update displayed apps without fetching again
            const displayedApps = this.allApps.slice(0, this.displayedItemsCount);
            this.appDATA = {
              data: displayedApps,
              meta: {
                pagination: {
                  total: this.allApps.length
                }
              }
            };
            // Don't scroll to top - maintain current scroll position
          }
        }
      } else {
        // If page is removed from URL, reset to first page
        if (this.displayedItemsCount !== 8) {
          this.displayedItemsCount = 8;
          const displayedApps = this.allApps.slice(0, this.displayedItemsCount);
          this.appDATA = {
            data: displayedApps,
            meta: {
              pagination: {
                total: this.allApps.length
              }
            }
          };
        }
      }
    },
    // Watch the "type" query parameter in the URL
    "$route.query.type"() {
      // Reset displayed items when type changes
      this.displayedItemsCount = 8;
      this.getAppQuery();
    },
    // Watch the "category" query parameter in the URL
    "$route.query.category"(newCategory) {
      if (newCategory !== this.selectedCategory) {
        this.selectedCategory = newCategory || null;
        this.displayedItemsCount = 8;
        this.getAppQuery();
      }
    },
    // Watch appDATA.data to check marquee when data changes
    'appDATA.data': {
      handler() {
        if (this.appDATA?.data && this.appDATA.data.length > 0) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.checkCategoryMarquee();
            }, 150);
          });
        }
      },
      immediate: false
    },
    // Watch searchQuery to pause/resume typing animation
    searchQuery(newValue) {
      if (newValue.trim() !== '') {
        this.pauseTyping = true;
      } else {
        this.pauseTyping = false;
      }
    },
  },
  async mounted() {
    // Check if there's a "category" query parameter in the URL
    if (this.$route.query.category) {
      this.selectedCategory = this.$route.query.category;
    }
    
    // Check if there's a "page" query parameter in the URL
    if (this.$route.query.page) {
      const page = parseInt(this.$route.query.page);
      if (!isNaN(page) && page > 0) {
        // Set displayedItemsCount based on page number
        // page 1 = 16 items, page 2 = 32 items, etc.
        this.displayedItemsCount = page * this.pageSize;
      }
    }
    
    // Detect user's country for currency display (runs in background)
    this.detectUserCountry();

    // Start app data fetch immediately
    const appFetch = this.getAppQuery();
    
    // Background fetch categories (don't block initial render)
    this.fetchCategories();
    
    await appFetch;

    // Use idle callback for non-critical initialization to reduce TBT
    const idleTask = () => {
      // Check category marquee after data loads
      this.$nextTick(() => {
        setTimeout(() => {
          this.checkCategoryMarquee();
        }, 100);
      });

      // Inject schema markup for SEO
      try {
        const schemas = [
          generateWebSiteSchema({
            name: "Meander Software",
            description: "Meander Software is an AI-enabled software development company building scalable web, mobile, and enterprise solutions."
          }),
          generateOrganizationSchema({
            name: "Meander Software",
            description: "Meander Software is an AI-enabled software development company building scalable web, mobile, and enterprise solutions. We deliver top-tier mobile app development, SaaS development, AI solutions, DevOps, cloud engineering, and automation for startups, enterprises, SMBs, healthcare, logistics, fintech, and education sectors.",
            email: "info@meander.software",
            url: "https://meander.software"
          })
        ];

        // Add FAQ Schema for homepage FAQs to optimize Google tracking
        const faqSchema = generateFAQSchema(this.homepageFaqs);
        if (faqSchema) {
          schemas.push(faqSchema);
        }

        injectSchemaMarkup(schemas);
      } catch (err) {
        console.error("Schema injection error:", err);
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(idleTask, { timeout: 3000 });
    } else {
      setTimeout(idleTask, 1000);
    }
    
    // Start typing animation for placeholder with a delay to save CPU during initial render
    setTimeout(() => {
      this.startTypingAnimation();
    }, 3000);
    
    // Add scroll listener for infinite scroll
    this.scrollListener = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.scrollListener, { passive: true });
    
    // Add resize listener to track window width for image optimization
    this.resizeListener = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener('resize', this.resizeListener);

    // Set meta tags
    this.setMetaTags();
  },
  beforeUnmount() {
    // Clean up typing animation interval
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
    }
    
    // Remove scroll listener
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }
    
    // Remove resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  },
};

</script>